// FILE: MessagesTimeline.tsx
// Purpose: Renders the virtualized chat transcript, including user bubbles, assistant markdown, and work logs.
// Layer: Web chat presentation component
// Exports: MessagesTimeline

import { type MessageId, ThreadId, type TurnId } from "@taskin-code/contracts";
import { formatAttachmentSize } from "@taskin-code/shared/attachmentKinds";
import { resolveAttachmentDownloadUrl } from "~/lib/attachmentUrls";
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from "react";
import {
  measureElement as measureVirtualElement,
  type VirtualItem,
  type Virtualizer,
  useVirtualizer,
} from "@tanstack/react-virtual";
import { deriveTimelineEntries, formatElapsed } from "../../session-logic";
import { AUTO_SCROLL_BOTTOM_THRESHOLD_PX } from "../../chat-scroll";
import { type TurnDiffSummary } from "../../types";
import ChatMarkdown from "../ChatMarkdown";
import {
  ArrowsMinimizeIcon,
  BookIcon,
  BotIcon,
  CheckIcon,
  CircleAlertIcon,
  EraserIcon,
  EyeIcon,
  GlobeIcon,
  HammerIcon,
  HistoryIcon,
  LoaderIcon,
  type LucideIcon,
  SquarePenIcon,
  TerminalIcon,
  Undo2Icon,
  ZapIcon,
} from "~/lib/icons";
import { Button } from "../ui/button";
import { clamp } from "effect/Number";
import {
  estimateTimelineMessageHeight,
  estimateTimelineWorkGroupHeight,
  WORK_ENTRY_DETAIL_MAX_HEIGHT_PX,
} from "../timelineHeight";
import { buildExpandedImagePreview, ExpandedImagePreview } from "./ExpandedImagePreview";
import { ProposedPlanCard } from "./ProposedPlanCard";
import { DiffStatLabel } from "./DiffStatLabel";
import { VscodeEntryIcon } from "./VscodeEntryIcon";
import { MessageCopyButton } from "./MessageCopyButton";
import {
  computeStableMessagesTimelineRows,
  deriveMessagesTimelineRows,
  type TimelineLatestTurn,
  extractWorkEntryAttachmentImages,
  hasOnlyToolToneEntries,
  hasWorkEntryAttachmentImages,
  WORK_ENTRY_ATTACHMENT_PREVIEW_MAX_HEIGHT_PX,
  type MessagesTimelineRow,
  normalizeCompactToolLabel,
  resolveAssistantMessageCopyState,
  resolveMessageAuthorPresentation,
  type StableMessagesTimelineRowsState,
  type WorkEntryAttachmentImage,
} from "./MessagesTimeline.logic";
import { QuotedContextInlineChip } from "./QuotedContextInlineChip";
import { formatWorkingTimer, WorkingTimer } from "./WorkingTimer";
import { describeSystemNotice } from "./SystemNoticeRow.logic";
import { PinnedUserMessage } from "./PinnedUserMessage";
import { UserInputWorkEntryRow } from "./UserInputWorkEntryRow";
import {
  createFrameScheduler,
  type PinnedUserMessageCandidate,
  resolvePinnedUserMessageIndex,
} from "./PinnedUserMessage.logic";
import { deriveDisplayedUserMessageState } from "~/lib/displayedUserMessage";
import {
  inferQuotedContextKindFromHeader,
  type ParsedQuotedContextEntry,
} from "~/lib/quotedContext";
import { cn } from "~/lib/utils";
import { useUserDirectoryMap } from "~/lib/userDirectoryReactQuery";
import { UserAvatar } from "../UserAvatar";
import {
  DEFAULT_CHAT_FONT_SIZE_PX,
  normalizeChatFontSizePx,
  type TimestampFormat,
} from "../../appSettings";
import { formatShortTimestamp } from "../../timestampFormat";
import {
  buildInlineTerminalContextText,
  formatInlineQuotedContextLabelFromHeader,
  textContainsInlineTerminalContextLabels,
} from "./userMessageTerminalContexts";
import { splitPromptIntoDisplaySegments } from "~/composer-editor-mentions";
import {
  COMPOSER_INLINE_CHIP_LABEL_CLASS_NAME,
  COMPOSER_INLINE_AGENT_CHIP_CLASS_NAME,
  COMPOSER_INLINE_AGENT_CHIP_ICON_CLASS_NAME,
  COMPOSER_INLINE_SKILL_CHIP_CLASS_NAME,
  COMPOSER_INLINE_SKILL_CHIP_ICON_CLASS_NAME,
  COMPOSER_INLINE_SKILL_CHIP_ICON_SVG,
  formatComposerSkillChipLabel,
} from "../composerInlineChip";
import { getChatTranscriptLineHeightPx, getChatTranscriptTextStyle } from "./chatTypography";
import { DisclosureChevron } from "../ui/DisclosureChevron";
import { LoadingState } from "../ui/loading-state";
import { getAppTypographyScale, type AppTypographyScale } from "../../lib/appTypography";
import { formatContextWindowTokens } from "../../lib/contextWindow";
import {} from "../../lib/subagentPresentation";
import { RiRobot3Line } from "react-icons/ri";

const ALWAYS_UNVIRTUALIZED_TAIL_ROWS = 8;
/**
 * How close to the top counts as "reaching the beginning".
 *
 * Roughly a screenful of slack, so the next page is already in flight by the
 * time the user actually gets there and the transcript never shows a hard stop
 * it could have avoided.
 */
const LOAD_OLDER_HISTORY_THRESHOLD_PX = 400;

/** Breathing room left between the pinned row and the message it jumps to. */
const PINNED_USER_MESSAGE_JUMP_GAP_PX = 8;

const SkillCubeIcon: LucideIcon = (props) => (
  <svg {...props} viewBox="0 0 24 24" fill="none">
    <path
      d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="m3.3 7 8.7 5 8.7-5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 22V12"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const AgentTaskIcon: LucideIcon = (props) => (
  <RiRobot3Line className={props.className} style={props.style} />
);

const DEFAULT_AGENT_COLOR = { bg: "rgb(245 158 11 / 0.15)", text: "rgb(245 158 11)" };
const AGENT_COLOR_STYLES: Record<string, { bg: string; text: string }> = {
  violet: { bg: "rgb(139 92 246 / 0.15)", text: "rgb(139 92 246)" },
  fuchsia: { bg: "rgb(217 70 239 / 0.15)", text: "rgb(217 70 239)" },
  teal: { bg: "rgb(20 184 166 / 0.15)", text: "rgb(20 184 166)" },
  cyan: { bg: "rgb(6 182 212 / 0.15)", text: "rgb(6 182 212)" },
  amber: DEFAULT_AGENT_COLOR,
  orange: { bg: "rgb(249 115 22 / 0.15)", text: "rgb(249 115 22)" },
};

function basename(value: string): string {
  const slash = Math.max(value.lastIndexOf("/"), value.lastIndexOf("\\"));
  return slash >= 0 ? value.slice(slash + 1) : value;
}

/**
 * Whether a collapsible tool group starts open, absent an explicit toggle by the
 * user (that one always wins, and is applied by the callers via `??`).
 *
 * A group open because the turn is still running closes itself once the turn
 * settles. A group holding a capture stays open: the point of the collapse is
 * hiding tool noise, and an image the user asked for is the answer, not noise.
 * Both the renderers and the virtualizer's size estimator go through here, so
 * the estimate cannot drift from what is actually laid out.
 */
function workGroupStartsExpanded(
  entries: ReadonlyArray<TimelineWorkEntry> | undefined,
  isActive: boolean | undefined,
): boolean {
  return (isActive ?? false) || hasWorkEntryAttachmentImages(entries);
}

/** Cache entry backing `estimateSize`'s per-row memoization; see its usage below. */
interface RowEstimateSizeCacheEntry {
  timelineWidthPx: number | null;
  normalizedChatFontSizePx: number;
  expandedWorkGroups: Record<string, boolean>;
  expandedWorkEntries: Record<string, boolean>;
  allDirectoriesExpandedByTurnId: Record<string, boolean>;
  size: number;
}

interface MessagesTimelineProps {
  hasMessages: boolean;
  isWorking: boolean;
  activeTurnInProgress: boolean;
  activeTurnStartedAt: string | null;
  emptyStateContent?: ReactNode;
  /**
   * The transcript has not arrived yet. Distinct from "no messages": both render
   * an empty pane, but only one of them is an answer.
   */
  isLoadingTranscript?: boolean;
  /**
   * The thread has history older than what was loaded. Distinct from
   * `isLoadingTranscript`: the transcript is here, it just does not start at
   * the beginning of the conversation.
   */
  hasMoreHistory?: boolean;
  isLoadingOlderHistory?: boolean;
  /**
   * Fetch the next page back. Called at most once per scroll into the top zone;
   * the caller is responsible for ignoring re-entrant calls while one is in
   * flight (this component only knows what `isLoadingOlderHistory` tells it).
   */
  onLoadOlderHistory?: () => void;
  scrollContainer: HTMLDivElement | null;
  timelineEntries: ReturnType<typeof deriveTimelineEntries>;
  /**
   * One-line summary of where the thread stands, regenerated when a turn settles.
   */
  recap?: { text: string; generatedAt: string } | null | undefined;
  turnDiffSummaryByAssistantMessageId: Map<MessageId, TurnDiffSummary>;
  nowIso?: string;
  expandedWorkGroups: Record<string, boolean>;
  onToggleWorkGroup: (groupId: string) => void;
  expandedWorkEntries: Record<string, boolean>;
  onToggleWorkEntry: (entryId: string) => void;
  onOpenTurnDiff: (turnId: TurnId, filePath?: string) => void;
  onOpenThread?: (threadId: ThreadId) => void;
  /**
   * Questions the agent is still waiting on, by request id.
   *
   * A "User input requested" row is only worth clicking while its question is
   * actually open; once answered it is history, and offering to reopen it would
   * promise something the composer cannot deliver.
   */
  openUserInputRequestIds?: ReadonlySet<string>;
  onReopenUserInput?: (requestId: string) => void;
  revertTurnCountByUserMessageId: Map<MessageId, number>;
  onRevertUserMessage: (messageId: MessageId) => void;
  isRevertingCheckpoint: boolean;
  onImageExpand: (preview: ExpandedImagePreview) => void;
  onTimelineHeightChange?: () => void;
  markdownCwd: string | undefined;
  resolvedTheme: "light" | "dark";
  chatFontSizePx?: number;
  timestampFormat: TimestampFormat;
  workspaceRoot: string | undefined;
  /**
   * The thread's latest turn, used to fold settled turns behind a
   * "Trabajado por ..." row. Without one, nothing folds.
   */
  latestTurn?: TimelineLatestTurn | null | undefined;
  /**
   * Whether that turn has finished. Passed in rather than derived here because
   * settling also depends on session state the transcript does not see.
   */
  latestTurnSettled?: boolean | undefined;
}

export const MessagesTimeline = memo(function MessagesTimeline({
  hasMessages,
  isWorking,
  activeTurnInProgress,
  activeTurnStartedAt,
  hasMoreHistory = false,
  isLoadingOlderHistory = false,
  onLoadOlderHistory,
  scrollContainer,
  timelineEntries,
  recap,
  turnDiffSummaryByAssistantMessageId,
  nowIso,
  expandedWorkGroups,
  onToggleWorkGroup,
  expandedWorkEntries,
  onToggleWorkEntry,
  onOpenTurnDiff,
  onOpenThread,
  openUserInputRequestIds,
  onReopenUserInput,
  revertTurnCountByUserMessageId,
  onRevertUserMessage,
  isRevertingCheckpoint,
  onImageExpand,
  onTimelineHeightChange,
  markdownCwd,
  resolvedTheme,
  chatFontSizePx = DEFAULT_CHAT_FONT_SIZE_PX,
  timestampFormat,
  workspaceRoot,
  emptyStateContent,
  isLoadingTranscript = false,
  latestTurn = null,
  latestTurnSettled = false,
}: MessagesTimelineProps) {
  const normalizedChatFontSizePx = normalizeChatFontSizePx(chatFontSizePx);
  const appTypographyScale = useMemo(
    () => getAppTypographyScale(normalizedChatFontSizePx),
    [normalizedChatFontSizePx],
  );
  const chatTypographyStyle = useMemo(
    () => getChatTranscriptTextStyle(normalizedChatFontSizePx),
    [normalizedChatFontSizePx],
  );
  // Fetched once here rather than per row: renderRowContent below is a plain
  // function called inside a loop, not a component, so this is the one hook
  // call a hundred-row timeline would otherwise risk repeating.
  const userDirectoryMap = useUserDirectoryMap();
  const timelineRootRef = useRef<HTMLDivElement | null>(null);
  const virtualSpacerRef = useRef<HTMLDivElement | null>(null);
  const pinnedElementRef = useRef<HTMLDivElement | null>(null);
  // Unvirtualized tail rows are absent from the virtualizer's measurement cache,
  // so the pin resolver reads their offsets from these (always mounted) nodes.
  const tailRowElementsRef = useRef<Map<number, HTMLElement>>(new Map());
  const [timelineWidthPx, setTimelineWidthPx] = useState<number | null>(null);
  const [expandedFileChangesByMessageId, setExpandedFileChangesByMessageId] = useState<
    Record<string, boolean>
  >({});
  // Deliberately local and ephemeral: a fold the reader opened is about the
  // question they are asking right now, not a preference worth outliving the
  // thread.
  const [expandedTurnIds, setExpandedTurnIds] = useState<ReadonlySet<TurnId>>(() => new Set());
  const onToggleTurnFold = useCallback((turnId: TurnId) => {
    setExpandedTurnIds((existing) => {
      const next = new Set(existing);
      if (next.has(turnId)) {
        next.delete(turnId);
      } else {
        next.add(turnId);
      }
      return next;
    });
  }, []);

  useLayoutEffect(() => {
    const timelineRoot = timelineRootRef.current;
    if (!timelineRoot) return;

    let lastHeight = -1;
    const syncRootSize = () => {
      const { width: nextWidth, height: nextHeight } = timelineRoot.getBoundingClientRect();
      setTimelineWidthPx((previousWidth) => {
        if (previousWidth !== null && Math.abs(previousWidth - nextWidth) < 0.5) {
          return previousWidth;
        }
        return nextWidth;
      });

      // Notify ChatView when async row measurement changes the rendered height so
      // stick-to-bottom can settle again after images or virtual rows expand.
      const heightChanged = lastHeight >= 0 && Math.abs(nextHeight - lastHeight) >= 0.5;
      lastHeight = nextHeight;
      if (heightChanged) {
        onTimelineHeightChange?.();
      }
    };

    syncRootSize();

    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      syncRootSize();
    });
    observer.observe(timelineRoot);
    return () => {
      observer.disconnect();
    };
  }, [hasMessages, isWorking, onTimelineHeightChange]);

  const rawRows = useMemo(
    () =>
      deriveMessagesTimelineRows({
        timelineEntries,
        isWorking,
        activeTurnStartedAt,
        turnDiffSummaryByAssistantMessageId,
        revertTurnCountByUserMessageId,
        latestTurn,
        latestTurnSettled,
        expandedTurnIds,
        recap,
      }),
    [
      timelineEntries,
      isWorking,
      activeTurnStartedAt,
      turnDiffSummaryByAssistantMessageId,
      revertTurnCountByUserMessageId,
      latestTurn,
      latestTurnSettled,
      expandedTurnIds,
      recap,
    ],
  );
  const rows = useStableRows(rawRows);

  const firstUnvirtualizedRowIndex = useMemo(() => {
    const firstTailRowIndex = Math.max(rows.length - ALWAYS_UNVIRTUALIZED_TAIL_ROWS, 0);
    if (!activeTurnInProgress) return firstTailRowIndex;

    // We intentionally keep a small live tail outside virtualization so the
    // currently active turn can expand without the user seeing rows jump in and
    // out of measurement. If this area ever becomes the next perf bottleneck,
    // this is the pivot point to shrink first.
    const turnStartedAtMs =
      typeof activeTurnStartedAt === "string" ? Date.parse(activeTurnStartedAt) : Number.NaN;
    let firstCurrentTurnRowIndex = -1;
    if (!Number.isNaN(turnStartedAtMs)) {
      firstCurrentTurnRowIndex = rows.findIndex((row) => {
        if (row.kind === "working") return true;
        if (!row.createdAt) return false;
        const rowCreatedAtMs = Date.parse(row.createdAt);
        return !Number.isNaN(rowCreatedAtMs) && rowCreatedAtMs >= turnStartedAtMs;
      });
    }

    if (firstCurrentTurnRowIndex < 0) {
      firstCurrentTurnRowIndex = rows.findIndex(
        (row) => row.kind === "message" && row.message.streaming,
      );
    }

    if (firstCurrentTurnRowIndex < 0) return firstTailRowIndex;

    for (let index = firstCurrentTurnRowIndex - 1; index >= 0; index -= 1) {
      const previousRow = rows[index];
      if (!previousRow || previousRow.kind !== "message") continue;
      // A server-authored notice opens a turn exactly like a user message does.
      if (previousRow.message.role === "user" || previousRow.message.role === "system") {
        return Math.min(index, firstTailRowIndex);
      }
      if (previousRow.message.role === "assistant" && !previousRow.message.streaming) {
        break;
      }
    }

    return Math.min(firstCurrentTurnRowIndex, firstTailRowIndex);
  }, [activeTurnInProgress, activeTurnStartedAt, rows]);

  const virtualizedRowCount = clamp(firstUnvirtualizedRowIndex, {
    minimum: 0,
    maximum: rows.length,
  });
  const [allDirectoriesExpandedByTurnId] = useState<Record<string, boolean>>({});
  const userMessageIdByAssistantMessageId = useMemo(() => {
    const map = new Map<MessageId, MessageId>();
    let lastUserMessageId: MessageId | null = null;
    for (const row of rows) {
      if (row.kind !== "message") continue;
      if (row.message.role === "user") {
        lastUserMessageId = row.message.id;
      } else if (row.message.role === "system") {
        // A turn the server started has no user message behind it, so its answer
        // must not offer to revert one the user wrote before it.
        lastUserMessageId = null;
      } else if (row.message.role === "assistant" && lastUserMessageId) {
        map.set(row.message.id, lastUserMessageId);
      }
    }
    return map;
  }, [rows]);

  // estimateSize runs on every scroll frame, so only clone a run when one of its
  // rows is really open (the common case allocates nothing).
  const withExpandedEntries = useCallback(
    (entries: ReadonlyArray<TimelineWorkEntry> | undefined): ReadonlyArray<TimelineWorkEntry> => {
      if (!entries || !entries.some((entry) => expandedWorkEntries[entry.id])) {
        return entries ?? [];
      }
      return entries.map((entry) =>
        expandedWorkEntries[entry.id] ? { ...entry, expanded: true } : entry,
      );
    },
    [expandedWorkEntries],
  );

  const estimateSizeCacheRef = useRef(
    new WeakMap<MessagesTimelineRow, RowEstimateSizeCacheEntry>(),
  );
  const computeRowEstimateSize = useCallback(
    (row: MessagesTimelineRow): number => {
      if (row.kind === "work") {
        return estimateTimelineWorkGroupHeight(withExpandedEntries(row.groupedEntries), {
          expanded: hasOnlyToolToneEntries(row.groupedEntries)
            ? (expandedWorkGroups[row.id] ??
              workGroupStartsExpanded(row.groupedEntries, row.isActive))
            : true,
        });
      }
      if (row.kind === "proposed-plan") {
        return estimateTimelineProposedPlanHeight(row.proposedPlan, normalizedChatFontSizePx);
      }
      if (row.kind === "working") return 40;
      if (row.kind === "turn-fold") return TURN_FOLD_ROW_HEIGHT_PX;
      if (row.kind === "recap") {
        // Two hairlines plus the wrapped line; ~64 characters fit per line at the
        // ui-sm size the row uses.
        return 26 + Math.ceil(row.text.length / 64) * 18;
      }
      const turnSummary = row.assistantTurnDiffSummary;
      const messageHeightInput = { ...row.message };
      if (row.message.role === "assistant" && hasOnlyToolToneEntries(row.inlineWorkEntries)) {
        Object.assign(messageHeightInput, {
          inlineToolEntries: withExpandedEntries(row.inlineWorkEntries),
          inlineToolExpanded: row.inlineWorkGroupId
            ? (expandedWorkGroups[row.inlineWorkGroupId] ??
              workGroupStartsExpanded(row.inlineWorkEntries, row.inlineWorkActive))
            : true,
        });
      }
      if (turnSummary) {
        Object.assign(messageHeightInput, {
          diffSummaryFiles: turnSummary.files,
          diffSummaryAllDirectoriesExpanded:
            allDirectoriesExpandedByTurnId[turnSummary.turnId] ?? true,
        });
      }
      return estimateTimelineMessageHeight(messageHeightInput, {
        timelineWidthPx,
        chatFontSizePx: normalizedChatFontSizePx,
      });
    },
    [
      allDirectoriesExpandedByTurnId,
      expandedWorkGroups,
      normalizedChatFontSizePx,
      timelineWidthPx,
      withExpandedEntries,
    ],
  );

  const rowVirtualizer = useVirtualizer({
    count: virtualizedRowCount,
    getScrollElement: () => scrollContainer,
    // Use stable row ids so virtual measurements do not leak across thread switches.
    getItemKey: (index: number) => rows[index]?.id ?? index,
    // Keep pre-measure placements close to the final layout so fast scrolls do not visually stack rows.
    estimateSize: (index: number) => {
      const row = rows[index];
      if (!row) return 96;
      // Rows are stable objects across renders when their content has not
      // changed (see useStableRows), so a WeakMap keyed on the row itself lets
      // an unrelated re-render (e.g. a streaming tick on a different row) skip
      // recomputing every mounted row's estimate. The cache entry also pins
      // down every other input the estimate reads, so it is invalidated the
      // moment any of them actually changes.
      const cache = estimateSizeCacheRef.current;
      const cached = cache.get(row);
      if (
        cached &&
        cached.timelineWidthPx === timelineWidthPx &&
        cached.normalizedChatFontSizePx === normalizedChatFontSizePx &&
        cached.expandedWorkGroups === expandedWorkGroups &&
        cached.expandedWorkEntries === expandedWorkEntries &&
        cached.allDirectoriesExpandedByTurnId === allDirectoriesExpandedByTurnId
      ) {
        return cached.size;
      }

      const size = computeRowEstimateSize(row);
      cache.set(row, {
        timelineWidthPx,
        normalizedChatFontSizePx,
        expandedWorkGroups,
        expandedWorkEntries,
        allDirectoriesExpandedByTurnId,
        size,
      });
      return size;
    },
    // We still dynamically measure rows because assistant markdown, images and
    // diff cards do not have stable heights. If we revisit the virtualizer
    // strategy, this ref is the main seam where we can switch to fixed or
    // selectively measured rows.
    measureElement: measureVirtualElement,
    useAnimationFrameWithResizeObserver: true,
    overscan: 4,
  });
  const pendingMeasureFrameRef = useRef<number | null>(null);
  // Coalesce all local "please remeasure" triggers into one RAF. The hot path
  // here is less about any single measure and more about several observers
  // firing back-to-back while the user is scrolling.
  const scheduleVirtualizerMeasure = useCallback(() => {
    if (pendingMeasureFrameRef.current !== null) return;
    pendingMeasureFrameRef.current = window.requestAnimationFrame(() => {
      pendingMeasureFrameRef.current = null;
      rowVirtualizer.measure();
      // `measure()` throws away every recorded size, so each row falls back to
      // `estimateSize` — which this file deliberately biases upward. The rows
      // on screen do not change size, so their ResizeObserver never fires and
      // nothing puts the real heights back: the surplus stays as a blank gap
      // between rows until the user scrolls far enough to remount them.
      // Re-measuring what is mounted, in the same frame, keeps the estimates
      // for the rows nobody can see and the truth for the rows they can.
      const spacer = virtualSpacerRef.current;
      if (!spacer) return;
      // Forces the measurement pass the reset invalidated, so the re-measure
      // below compares against the fresh estimate and is recorded.
      rowVirtualizer.getVirtualItems();
      for (const node of spacer.querySelectorAll<HTMLElement>("[data-index]")) {
        rowVirtualizer.measureElement(node);
      }
    });
  }, [rowVirtualizer]);
  useEffect(() => {
    if (timelineWidthPx === null) return;
    scheduleVirtualizerMeasure();
  }, [scheduleVirtualizerMeasure, timelineWidthPx]);
  useLayoutEffect(() => {
    if (!scrollContainer || typeof ResizeObserver === "undefined") return;

    let lastViewportWidth = -1;
    let lastViewportHeight = -1;
    // Re-measure when the scroll viewport changes because composer/panel chrome
    // can steal vertical space without remounting the timeline.
    const syncViewportSize = () => {
      const nextViewportWidth = scrollContainer.clientWidth;
      const nextViewportHeight = scrollContainer.clientHeight;
      if (
        Math.abs(nextViewportWidth - lastViewportWidth) < 0.5 &&
        Math.abs(nextViewportHeight - lastViewportHeight) < 0.5
      ) {
        return;
      }
      lastViewportWidth = nextViewportWidth;
      lastViewportHeight = nextViewportHeight;
      scheduleVirtualizerMeasure();
    };

    syncViewportSize();
    const observer = new ResizeObserver(() => {
      syncViewportSize();
    });
    observer.observe(scrollContainer);
    return () => {
      observer.disconnect();
    };
  }, [scheduleVirtualizerMeasure, scrollContainer]);
  useEffect(() => {
    scheduleVirtualizerMeasure();
  }, [
    expandedWorkGroups,
    expandedWorkEntries,
    expandedFileChangesByMessageId,
    allDirectoriesExpandedByTurnId,
    normalizedChatFontSizePx,
    scheduleVirtualizerMeasure,
  ]);
  useEffect(() => {
    // TanStack can compensate for late measurements by shifting scroll
    // position. This keeps the bottom anchored, but it is also one of the
    // places to revisit if future "scroll fights the user" regressions appear.
    rowVirtualizer.shouldAdjustScrollPositionOnItemSizeChange = (_item, _delta, instance) => {
      const viewportHeight = instance.scrollRect?.height ?? 0;
      const scrollOffset = instance.scrollOffset ?? 0;
      const remainingDistance = instance.getTotalSize() - (scrollOffset + viewportHeight);
      return remainingDistance > AUTO_SCROLL_BOTTOM_THRESHOLD_PX;
    };
    return () => {
      rowVirtualizer.shouldAdjustScrollPositionOnItemSizeChange = undefined;
    };
  }, [rowVirtualizer]);
  const onTimelineImageLoad = useCallback(() => {
    scheduleVirtualizerMeasure();
  }, [scheduleVirtualizerMeasure]);
  useEffect(() => {
    return () => {
      const frame = pendingMeasureFrameRef.current;
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, []);

  const userMessageRows = useMemo(() => {
    const entries: PinnedUserMessageRow[] = [];
    rows.forEach((row, rowIndex) => {
      if (row.kind !== "message" || row.message.role !== "user") return;
      entries.push({ rowIndex, messageId: row.message.id, message: row.message });
    });
    return entries;
  }, [rows]);
  useOlderHistoryLoader({
    scrollContainer,
    rows,
    rowVirtualizer,
    hasMoreHistory,
    isLoadingOlderHistory,
    onLoadOlderHistory,
  });
  const { pinnedRow, scrollToPinnedRow, scrollbarWidthPx } = usePinnedUserMessage({
    scrollContainer,
    userMessageRows,
    virtualizedRowCount,
    rowVirtualizer,
    virtualSpacerRef,
    tailRowElementsRef,
    pinnedElementRef,
    layoutRevision: `${timelineWidthPx ?? 0}:${normalizedChatFontSizePx}`,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const nonVirtualizedRows = rows.slice(virtualizedRowCount);
  const setTailRowElement = useCallback(
    (rowIndex: number) => (element: HTMLDivElement | null) => {
      if (element) {
        tailRowElementsRef.current.set(rowIndex, element);
      } else {
        tailRowElementsRef.current.delete(rowIndex);
      }
    },
    [],
  );
  const toggleFileChangesExpanded = useCallback((messageId: MessageId) => {
    setExpandedFileChangesByMessageId((current) => ({
      ...current,
      [messageId]: !(current[messageId] ?? true),
    }));
  }, []);

  const sharedContext = useMemo<TimelineRowSharedContext>(
    () => ({
      expandedWorkGroups,
      onToggleWorkGroup,
      onToggleTurnFold,
      appTypographyScale,
      expandedWorkEntries,
      onToggleWorkEntry,
      onImageExpand,
      onTimelineImageLoad,
      onOpenThread,
      openUserInputRequestIds,
      onReopenUserInput,
      timestampFormat,
      userDirectoryMap,
      resolvedTheme,
      chatTypographyStyle,
      isRevertingCheckpoint,
      isWorking,
      onRevertUserMessage,
      nowIso,
      markdownCwd,
      normalizedChatFontSizePx,
      onOpenTurnDiff,
      expandedFileChangesByMessageId,
      userMessageIdByAssistantMessageId,
      revertTurnCountByUserMessageId,
      toggleFileChangesExpanded,
      workspaceRoot,
    }),
    [
      expandedWorkGroups,
      onToggleWorkGroup,
      onToggleTurnFold,
      appTypographyScale,
      expandedWorkEntries,
      onToggleWorkEntry,
      onImageExpand,
      onTimelineImageLoad,
      onOpenThread,
      openUserInputRequestIds,
      onReopenUserInput,
      timestampFormat,
      userDirectoryMap,
      resolvedTheme,
      chatTypographyStyle,
      isRevertingCheckpoint,
      isWorking,
      onRevertUserMessage,
      nowIso,
      markdownCwd,
      normalizedChatFontSizePx,
      onOpenTurnDiff,
      expandedFileChangesByMessageId,
      userMessageIdByAssistantMessageId,
      revertTurnCountByUserMessageId,
      toggleFileChangesExpanded,
      workspaceRoot,
    ],
  );

  if (!hasMessages && !isWorking) {
    if (isLoadingTranscript) {
      return <LoadingState label="Cargando la conversación…" />;
    }
    if (emptyStateContent) {
      return <div className="flex h-full items-center justify-center">{emptyStateContent}</div>;
    }
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-sm text-muted-foreground/30">
          Send a message to start the conversation.
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Absolutely positioned like the pin, and for the same reason: it must
          not take space in the flow. A spinner that pushed the transcript down
          while a page was in flight — and let it snap back when the page
          landed — would undo the scroll compensation it is meant to accompany.
          Below the pin in z-order because the two never coincide in practice:
          the pin only appears once a user message has scrolled off the top. */}
      {isLoadingOlderHistory ? (
        <div
          data-scroll-anchor-ignore=""
          className="pointer-events-none absolute top-0 right-0 left-0 z-10 flex justify-center pt-2"
          style={{ marginRight: `${scrollbarWidthPx}px` }}
        >
          <span className="rounded-full border border-border/60 bg-background/90 px-3 py-1 text-xs text-muted-foreground shadow-sm backdrop-blur-sm">
            Cargando mensajes anteriores…
          </span>
        </div>
      ) : null}

      {/* Deliberately a sibling of the timeline root and absolutely positioned:
          `position: sticky` cannot work on the virtualized rows, and the root
          itself is an overflow box. Its containing block is the non-scrolling
          wrapper that already hosts the scroll-to-bottom button, so the pin
          stays put while the transcript scrolls under it — yet it still lives
          inside the scroll container, so wheel and touch gestures over it keep
          bubbling to the auto-scroll controller's handlers. */}
      {pinnedRow ? (
        <div
          data-scroll-anchor-ignore=""
          className="pointer-events-none absolute top-0 right-0 left-0 z-20 px-3 sm:px-5"
          style={{ marginRight: `${scrollbarWidthPx}px` }}
        >
          <div ref={pinnedElementRef} className="pointer-events-auto mx-auto w-full max-w-3xl">
            <PinnedUserMessage
              message={pinnedRow.message}
              chatFontSizePx={normalizedChatFontSizePx}
              onClick={scrollToPinnedRow}
            />
          </div>
        </div>
      ) : null}

      <div
        ref={timelineRootRef}
        data-timeline-root="true"
        className="mx-auto w-full min-w-0 max-w-3xl overflow-x-hidden"
      >
        {virtualizedRowCount > 0 && (
          <div
            ref={virtualSpacerRef}
            className="relative"
            style={{ height: `${rowVirtualizer.getTotalSize()}px` }}
          >
            {virtualRows.map((virtualRow: VirtualItem) => {
              const row = rows[virtualRow.index];
              if (!row) return null;

              return (
                <div
                  key={`virtual-row:${row.id}`}
                  data-index={virtualRow.index}
                  ref={rowVirtualizer.measureElement}
                  className="absolute left-0 top-0 w-full"
                  style={{ transform: `translateY(${virtualRow.start}px)` }}
                >
                  <TimelineRowContent row={row} shared={sharedContext} />
                </div>
              );
            })}
          </div>
        )}

        {nonVirtualizedRows.map((row, tailIndex) => (
          <div
            key={`non-virtual-row:${row.id}`}
            ref={setTailRowElement(virtualizedRowCount + tailIndex)}
          >
            <TimelineRowContent row={row} shared={sharedContext} />
          </div>
        ))}
      </div>
    </>
  );
});

interface TimelineRowSharedContext {
  expandedWorkGroups: Record<string, boolean>;
  onToggleWorkGroup: (groupId: string) => void;
  onToggleTurnFold: (turnId: TurnId) => void;
  appTypographyScale: AppTypographyScale;
  expandedWorkEntries: Record<string, boolean>;
  onToggleWorkEntry: (entryId: string) => void;
  onImageExpand: (preview: ExpandedImagePreview) => void;
  onTimelineImageLoad: () => void;
  onOpenThread: ((threadId: ThreadId) => void) | undefined;
  openUserInputRequestIds: ReadonlySet<string> | undefined;
  onReopenUserInput: ((requestId: string) => void) | undefined;
  timestampFormat: TimestampFormat;
  userDirectoryMap: ReturnType<typeof useUserDirectoryMap>;
  resolvedTheme: "light" | "dark";
  chatTypographyStyle: CSSProperties;
  isRevertingCheckpoint: boolean;
  isWorking: boolean;
  onRevertUserMessage: (messageId: MessageId) => void;
  nowIso: string | undefined;
  markdownCwd: string | undefined;
  normalizedChatFontSizePx: number;
  onOpenTurnDiff: (turnId: TurnId, filePath?: string) => void;
  expandedFileChangesByMessageId: Record<string, boolean>;
  userMessageIdByAssistantMessageId: Map<MessageId, MessageId>;
  revertTurnCountByUserMessageId: Map<MessageId, number>;
  toggleFileChangesExpanded: (messageId: MessageId) => void;
  workspaceRoot: string | undefined;
}

/**
 * One row's rendered content, wrapped in its own memo boundary so a timeline
 * re-render (every streaming tick) does not re-execute every mounted row's
 * render — only the row(s) whose `row` object or `shared` context actually
 * changed. `shared` bundles everything the row bodies close over that is not
 * part of `row` itself; MessagesTimeline builds it with useMemo so its
 * identity stays stable across ticks that do not touch any of its fields.
 */
const TimelineRowContent = memo(function TimelineRowContent({
  row,
  shared,
}: {
  row: MessagesTimelineRow;
  shared: TimelineRowSharedContext;
}) {
  return (
    <div
      className={cn(
        row.kind === "work" ||
          row.kind === "turn-fold" ||
          (row.kind === "message" && row.message.role === "assistant")
          ? "pb-2"
          : "pb-4",
        row.kind === "message" && row.message.role === "assistant" ? "group/assistant" : null,
      )}
      data-timeline-row-kind={row.kind}
      data-message-id={row.kind === "message" ? row.message.id : undefined}
      data-message-role={row.kind === "message" ? row.message.role : undefined}
    >
      {row.kind === "turn-fold" && (
        <TurnFoldRow
          label={row.label}
          expanded={row.expanded}
          fontSizePx={shared.appTypographyScale.uiSmPx}
          onToggle={() => shared.onToggleTurnFold(row.turnId)}
        />
      )}

      {row.kind === "work" &&
        (() => {
          const groupId = row.id;
          const groupedEntries = row.groupedEntries;
          const collapsible = hasOnlyToolToneEntries(groupedEntries);
          // Default state follows the turn: a run that can still grow stays open, a
          // settled one collapses — unless it holds a capture, which is the answer
          // and not tool noise. An explicit toggle by the user always wins.
          const isExpanded = collapsible
            ? (shared.expandedWorkGroups[groupId] ??
              workGroupStartsExpanded(groupedEntries, row.isActive))
            : true;

          return (
            <div>
              {collapsible && (
                <WorkGroupSummaryHeader
                  entries={groupedEntries}
                  expanded={isExpanded}
                  fontSizePx={shared.appTypographyScale.uiSmPx}
                  onToggle={() => shared.onToggleWorkGroup(groupId)}
                />
              )}
              <div
                className={cn(
                  "grid transition-[grid-template-rows,opacity] duration-200 ease-out",
                  isExpanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
                )}
              >
                <div
                  className={cn(
                    "min-h-0 overflow-hidden",
                    isExpanded ? null : "pointer-events-none",
                  )}
                >
                  <div
                    className={cn(
                      "space-y-0.5",
                      collapsible && "ml-[6px] border-l border-border/40 pl-2.5",
                    )}
                  >
                    {groupedEntries.map((workEntry) => (
                      <SimpleWorkEntryRow
                        key={`work-row:${workEntry.id}`}
                        workEntry={workEntry}
                        chatMetaFontSizePx={shared.appTypographyScale.chatMetaPx}
                        textFontSizePx={shared.appTypographyScale.uiSmPx}
                        density={prefersCompactWorkEntryRow(workEntry) ? "compact" : "default"}
                        expanded={shared.expandedWorkEntries[workEntry.id] ?? false}
                        onToggleExpanded={shared.onToggleWorkEntry}
                        onImageExpand={shared.onImageExpand}
                        onImageSettled={shared.onTimelineImageLoad}
                        {...(shared.onOpenThread ? { onOpenThread: shared.onOpenThread } : {})}
                        {...(shared.openUserInputRequestIds
                          ? { openUserInputRequestIds: shared.openUserInputRequestIds }
                          : {})}
                        {...(shared.onReopenUserInput
                          ? { onReopenUserInput: shared.onReopenUserInput }
                          : {})}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

      {/*
        A turn the server started itself — today, waking a delegating agent whose
        subagent finished. It gets its own row rather than a user bubble: nobody
        typed it, and without a row at all the turn below would appear out of
        nowhere.
      */}
      {row.kind === "message" && row.message.role === "system" && (
        <SystemNoticeRow
          text={row.message.text}
          fontSizePx={shared.appTypographyScale.chatMetaPx}
          createdAt={row.message.createdAt}
          timestampFormat={shared.timestampFormat}
        />
      )}

      {row.kind === "message" &&
        row.message.role === "user" &&
        (() => {
          const userImages = row.message.attachments ?? [];
          const imageOnlyAttachments = userImages.filter(
            (attachment) => attachment.type === "image",
          );
          const displayedUserMessage = deriveDisplayedUserMessageState(row.message.text, {
            hideImageOnlyBootstrapPrompt: userImages.length > 0,
          });
          const quotedContexts = displayedUserMessage.contexts;
          const showUserText =
            displayedUserMessage.visibleText.trim().length > 0 || quotedContexts.length > 0;
          const bubbleIsChipOnly =
            showUserText &&
            quotedContexts.length === 0 &&
            hasOnlyInlineSkillChips(displayedUserMessage.visibleText);
          const canRevertAgentWork = typeof row.revertTurnCount === "number";
          const messageAuthor = resolveMessageAuthorPresentation(
            row.message,
            shared.userDirectoryMap,
          );
          return (
            <div className="flex w-full">
              <div className="group flex w-full min-w-0 flex-col gap-px">
                {/* Keep user-message chrome outside the bubble so the message reads as one simple block. */}
                {userImages.length > 0 && (
                  <div
                    className={cn(
                      "flex max-w-[240px] flex-wrap gap-2 self-start",
                      showUserText && "mb-1",
                    )}
                  >
                    {userImages.map(
                      (attachment: NonNullable<TimelineMessage["attachments"]>[number]) =>
                        attachment.type === "file" ? (
                          <UserFileAttachmentChip
                            key={attachment.id}
                            attachment={attachment}
                            resolvedTheme={shared.resolvedTheme}
                          />
                        ) : (
                          <UserImageAttachmentThumbnail
                            key={attachment.id}
                            image={attachment}
                            userImages={imageOnlyAttachments}
                            onImageExpand={shared.onImageExpand}
                            onTimelineImageLoad={shared.onTimelineImageLoad}
                            resolvedTheme={shared.resolvedTheme}
                          />
                        ),
                    )}
                  </div>
                )}
                {showUserText && (
                  <div
                    className={cn(
                      "w-full min-w-0 rounded-lg border border-border/70 bg-secondary px-3",
                      bubbleIsChipOnly ? "py-0.5" : "pt-[3px] pb-[5px]",
                    )}
                  >
                    <UserMessageBody
                      text={displayedUserMessage.visibleText}
                      quotedContexts={quotedContexts}
                      chatTypographyStyle={shared.chatTypographyStyle}
                    />
                  </div>
                )}
                <div className="flex w-full items-center justify-end gap-1.5 pr-0.5">
                  <div className="flex items-center gap-1 opacity-0 transition-opacity duration-200 focus-within:opacity-100 group-hover:opacity-100">
                    {displayedUserMessage.copyText && (
                      <MessageCopyButton text={displayedUserMessage.copyText} />
                    )}
                    {canRevertAgentWork && (
                      <Button
                        type="button"
                        size="icon-xs"
                        variant="ghost"
                        className="size-auto rounded-none border-0 bg-transparent p-0 text-muted-foreground/55 shadow-none hover:bg-transparent hover:text-foreground focus-visible:ring-0 focus-visible:ring-offset-0"
                        disabled={shared.isRevertingCheckpoint || shared.isWorking}
                        onClick={() => shared.onRevertUserMessage(row.message.id)}
                        title="Revert to this message"
                        aria-label="Revert to this message"
                      >
                        <Undo2Icon className="size-3" />
                      </Button>
                    )}
                  </div>
                  {messageAuthor ? (
                    <UserAvatar
                      userId={messageAuthor.userId ?? row.message.id}
                      displayName={messageAuthor.displayName}
                      avatarFileName={messageAuthor.avatarFileName}
                      size="xs"
                      className="shrink-0"
                    />
                  ) : null}
                  <p
                    className="font-chat-code text-right text-muted-foreground/45"
                    style={{ fontSize: `${shared.appTypographyScale.uiTimestampPx}px` }}
                  >
                    {messageAuthor ? (
                      <>
                        <span>{messageAuthor.displayName}</span>
                        <span aria-hidden="true"> · </span>
                      </>
                    ) : null}
                    {formatShortTimestamp(row.message.createdAt, shared.timestampFormat)}
                  </p>
                </div>
              </div>
            </div>
          );
        })()}

      {row.kind === "message" &&
        row.message.role === "assistant" &&
        (() => {
          const messageText = row.message.text || (row.message.streaming ? "" : "(empty response)");
          const inlineToolEntries = hasOnlyToolToneEntries(row.inlineWorkEntries)
            ? row.inlineWorkEntries
            : [];
          const inlineToolGroupId =
            inlineToolEntries.length > 0 ? (row.inlineWorkGroupId ?? null) : null;
          const inlineToolExpanded =
            inlineToolGroupId !== null
              ? (shared.expandedWorkGroups[inlineToolGroupId] ??
                workGroupStartsExpanded(inlineToolEntries, row.inlineWorkActive))
              : false;
          const inlineWorkSummary =
            inlineToolEntries.length > 0
              ? null
              : formatInlineWorkSummary(row.inlineWorkEntries ?? []);
          const assistantCopyState = resolveAssistantMessageCopyState({
            text: row.message.text ?? null,
            showCopyButton: row.showAssistantCopyButton,
            streaming: row.message.streaming,
          });
          const turnSummary = row.assistantTurnDiffSummary;
          const fileDiffStatByPath = new Map(
            (turnSummary?.files ?? []).map((file) => [
              file.path,
              {
                additions: file.additions ?? 0,
                deletions: file.deletions ?? 0,
              },
            ]),
          );
          const hasGenericInlineFileChangeEntry = inlineToolEntries.some(
            (workEntry) =>
              isFileChangeWorkEntry(workEntry) && (workEntry.changedFiles?.length ?? 0) === 0,
          );
          const visibleRenderableInlineToolEntries = inlineToolEntries.filter(
            (workEntry) =>
              !(
                hasGenericInlineFileChangeEntry &&
                isFileChangeWorkEntry(workEntry) &&
                (workEntry.changedFiles?.length ?? 0) === 0
              ),
          );
          const inlineEditedFilesFromTurnSummary =
            hasGenericInlineFileChangeEntry && (turnSummary?.files.length ?? 0) > 0
              ? turnSummary!.files
              : [];
          const assistantMeta = row.message.streaming ? (
            shared.nowIso ? (
              [
                formatMessageMeta(
                  row.message.createdAt,
                  formatElapsed(row.durationStart, shared.nowIso),
                  shared.timestampFormat,
                ),
                inlineWorkSummary,
              ]
                .filter((value): value is string => Boolean(value))
                .join(" • ")
            ) : (
              <>
                <LiveMessageMeta
                  createdAt={row.message.createdAt}
                  durationStart={row.durationStart}
                  timestampFormat={shared.timestampFormat}
                />
                {inlineWorkSummary ? <> • {inlineWorkSummary}</> : null}
              </>
            )
          ) : (
            [
              formatMessageMeta(
                row.message.createdAt,
                formatElapsed(row.durationStart, row.message.completedAt),
                shared.timestampFormat,
              ),
              inlineWorkSummary,
            ]
              .filter((value): value is string => Boolean(value))
              .join(" • ")
          );
          return (
            <>
              <div className="min-w-0 px-1 py-0.5">
                <ChatMarkdown
                  text={messageText}
                  cwd={shared.markdownCwd}
                  isStreaming={Boolean(row.message.streaming)}
                  style={shared.chatTypographyStyle}
                  onImageExpand={shared.onImageExpand}
                />
                {visibleRenderableInlineToolEntries.length > 0 && (
                  <div className="mt-2.5">
                    {inlineToolGroupId && (
                      <WorkGroupSummaryHeader
                        entries={visibleRenderableInlineToolEntries}
                        expanded={inlineToolExpanded}
                        fontSizePx={shared.normalizedChatFontSizePx}
                        onToggle={() => shared.onToggleWorkGroup(inlineToolGroupId)}
                      />
                    )}
                    <div
                      className={cn(
                        "grid transition-[grid-template-rows,opacity] duration-200 ease-out",
                        inlineToolExpanded || !inlineToolGroupId
                          ? "grid-rows-[1fr] opacity-100"
                          : "grid-rows-[0fr] opacity-0",
                      )}
                    >
                      <div
                        className={cn(
                          "min-h-0 overflow-hidden",
                          inlineToolExpanded || !inlineToolGroupId ? null : "pointer-events-none",
                        )}
                      >
                        <div
                          className={cn(
                            "space-y-px",
                            inlineToolGroupId && "ml-[6px] border-l border-border/40 pl-2.5",
                          )}
                        >
                          {visibleRenderableInlineToolEntries.map((workEntry) => (
                            <SimpleWorkEntryRow
                              key={`inline-tool-row:${row.message.id}:${workEntry.id}`}
                              workEntry={workEntry}
                              chatMetaFontSizePx={shared.appTypographyScale.chatMetaPx}
                              textFontSizePx={shared.normalizedChatFontSizePx}
                              density="compact"
                              fileDiffStatByPath={fileDiffStatByPath}
                              expanded={shared.expandedWorkEntries[workEntry.id] ?? false}
                              onToggleExpanded={shared.onToggleWorkEntry}
                              onImageExpand={shared.onImageExpand}
                              onImageSettled={shared.onTimelineImageLoad}
                              onOpenTurnDiff={shared.onOpenTurnDiff}
                              {...(shared.onOpenThread
                                ? { onOpenThread: shared.onOpenThread }
                                : {})}
                              {...(shared.openUserInputRequestIds
                                ? { openUserInputRequestIds: shared.openUserInputRequestIds }
                                : {})}
                              {...(shared.onReopenUserInput
                                ? { onReopenUserInput: shared.onReopenUserInput }
                                : {})}
                              {...(turnSummary?.turnId ? { turnId: turnSummary.turnId } : {})}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {inlineEditedFilesFromTurnSummary.length > 0 && (
                  <div className="mt-2 space-y-0.5">
                    {inlineEditedFilesFromTurnSummary.map((file) => (
                      <button
                        key={`inline-summary-edit:${row.message.id}:${file.path}`}
                        type="button"
                        className="group flex w-full max-w-full items-baseline gap-1 px-0 py-[1px] text-left transition-opacity duration-150 hover:opacity-95"
                        title={file.path}
                        onClick={() => shared.onOpenTurnDiff(turnSummary!.turnId, file.path)}
                      >
                        <span
                          className="font-system-ui shrink-0 text-[#7b7b84]"
                          style={{ fontSize: `${shared.normalizedChatFontSizePx}px` }}
                        >
                          Edited
                        </span>
                        <span
                          className="font-system-ui max-w-[28rem] truncate group-hover:opacity-90"
                          style={{
                            fontSize: `${shared.normalizedChatFontSizePx}px`,
                            color: "var(--info-foreground)",
                          }}
                        >
                          {basename(file.path)}
                        </span>
                        {(file.additions ?? 0) + (file.deletions ?? 0) > 0 ? (
                          <span
                            className="font-chat-code shrink-0 tabular-nums whitespace-nowrap"
                            style={{ fontSize: `${shared.normalizedChatFontSizePx}px` }}
                          >
                            <DiffStatLabel
                              additions={file.additions ?? 0}
                              deletions={file.deletions ?? 0}
                            />
                          </span>
                        ) : null}
                      </button>
                    ))}
                  </div>
                )}
                {(() => {
                  if (!turnSummary) return null;
                  const checkpointFiles = turnSummary.files;
                  if (checkpointFiles.length === 0) return null;
                  const fileChangesExpanded =
                    shared.expandedFileChangesByMessageId[row.message.id] ?? true;
                  const correspondingUserMessageId = shared.userMessageIdByAssistantMessageId.get(
                    row.message.id,
                  );
                  const canUndo =
                    correspondingUserMessageId != null &&
                    shared.revertTurnCountByUserMessageId.has(correspondingUserMessageId);
                  return (
                    <div className="mt-5 overflow-hidden rounded-lg border border-border bg-neutral-50 dark:bg-neutral-900">
                      <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
                        <span
                          className="truncate font-normal text-foreground"
                          style={{ fontSize: shared.chatTypographyStyle.fontSize }}
                        >
                          {checkpointFiles.length === 1
                            ? "1 File changed"
                            : `${checkpointFiles.length} Files changed`}
                        </span>
                        <div className="flex items-center gap-4">
                          <button
                            type="button"
                            className="inline-flex items-center justify-center rounded-md p-1 text-muted-foreground/70 transition-colors hover:bg-background/60 hover:text-foreground/80"
                            aria-expanded={fileChangesExpanded}
                            aria-label={
                              fileChangesExpanded
                                ? "Collapse changed files list"
                                : "Expand changed files list"
                            }
                            onClick={() => shared.toggleFileChangesExpanded(row.message.id)}
                          >
                            <DisclosureChevron
                              open={fileChangesExpanded}
                              className="dark:text-muted-foreground/50"
                            />
                          </button>
                          {canUndo && (
                            <button
                              type="button"
                              className="flex items-center gap-1 text-muted-foreground transition-colors hover:text-foreground"
                              style={{ fontSize: shared.chatTypographyStyle.fontSize }}
                              onClick={() => shared.onRevertUserMessage(correspondingUserMessageId)}
                            >
                              Undo
                              <Undo2Icon className="size-3" />
                            </button>
                          )}
                        </div>
                      </div>
                      <div
                        className={cn(
                          "grid transition-[grid-template-rows,opacity] duration-220 ease-out",
                          fileChangesExpanded
                            ? "grid-rows-[1fr] opacity-100"
                            : "grid-rows-[0fr] opacity-0",
                        )}
                      >
                        <div
                          className={cn(
                            "min-h-0 overflow-hidden transition-transform duration-220 ease-out",
                            fileChangesExpanded
                              ? "translate-y-0"
                              : "-translate-y-1 pointer-events-none",
                          )}
                        >
                          <div className="bg-neutral-100 dark:bg-neutral-800/40">
                            {checkpointFiles.map((file) => (
                              <button
                                key={file.path}
                                type="button"
                                className="flex w-full items-center gap-2 px-3 py-1.5 text-left transition-colors hover:bg-muted/60"
                                onClick={() => shared.onOpenTurnDiff(turnSummary.turnId, file.path)}
                              >
                                <VscodeEntryIcon
                                  pathValue={file.path}
                                  kind="file"
                                  theme={shared.resolvedTheme}
                                  className="size-4 shrink-0 opacity-50 dark:opacity-30"
                                />
                                <span
                                  className="font-chat-code truncate font-normal text-neutral-900 dark:text-foreground dark:hover:text-foreground"
                                  style={{ fontSize: `${shared.appTypographyScale.chatCodePx}px` }}
                                >
                                  {file.path}
                                </span>
                                {(file.additions ?? 0) + (file.deletions ?? 0) > 0 && (
                                  <span
                                    className="font-chat-code ml-auto shrink-0 tabular-nums"
                                    style={{
                                      fontSize: `${shared.appTypographyScale.chatMetaPx}px`,
                                    }}
                                  >
                                    <DiffStatLabel
                                      additions={file.additions ?? 0}
                                      deletions={file.deletions ?? 0}
                                    />
                                  </span>
                                )}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
                <div className="mt-0.5 flex items-center gap-2">
                  <p
                    className="font-chat-code text-muted-foreground/45"
                    style={{ fontSize: `${shared.appTypographyScale.uiTimestampPx}px` }}
                  >
                    {assistantMeta}
                  </p>
                  {assistantCopyState.visible ? (
                    <div className="flex items-center opacity-0 transition-opacity duration-200 group-hover/assistant:opacity-100 focus-within:opacity-100">
                      <MessageCopyButton
                        text={assistantCopyState.text ?? ""}
                        size="icon-xs"
                        variant="outline"
                        className="border-border/50 bg-background/35 text-muted-foreground/45 shadow-none hover:border-border/70 hover:bg-background/55 hover:text-muted-foreground/70"
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            </>
          );
        })()}

      {row.kind === "proposed-plan" && (
        <div className="min-w-0 px-1 py-0.5">
          <ProposedPlanCard
            planMarkdown={row.proposedPlan.planMarkdown}
            cwd={shared.markdownCwd}
            workspaceRoot={shared.workspaceRoot}
            chatTypographyStyle={shared.chatTypographyStyle}
          />
        </div>
      )}

      {row.kind === "recap" && (
        <div className="my-3 flex items-start gap-3">
          <span className="mt-2 h-px flex-1 bg-border" />
          <span
            className="inline-flex max-w-[62ch] items-start gap-1.5 text-muted-foreground/85 font-system-ui"
            style={{ fontSize: `${shared.appTypographyScale.uiSmPx}px`, lineHeight: 1.6 }}
          >
            <HistoryIcon aria-hidden="true" className="mt-0.5 size-3 shrink-0 text-brand/70" />
            <span>{row.text}</span>
          </span>
          <span className="mt-2 h-px flex-1 bg-border" />
        </div>
      )}

      {row.kind === "working" && (
        <div>
          {row.label ? (
            <div className="my-3 flex items-center gap-3">
              <span className="h-px flex-1 bg-border" />
              <span
                className="inline-flex items-center gap-1 text-muted-foreground/80 font-system-ui"
                style={{ fontSize: `${shared.appTypographyScale.uiSmPx}px` }}
              >
                <span>
                  {formatWorkingLabel(row.label)}
                  {row.createdAt
                    ? ` ${shared.nowIso ? (formatWorkingTimer(row.createdAt, shared.nowIso) ?? "0s") : ""}`
                    : null}
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="h-1 w-1 rounded-full bg-muted-foreground/30 animate-pulse" />
                  <span className="h-1 w-1 rounded-full bg-muted-foreground/30 animate-pulse [animation-delay:200ms]" />
                  <span className="h-1 w-1 rounded-full bg-muted-foreground/30 animate-pulse [animation-delay:400ms]" />
                </span>
              </span>
              <span className="h-px flex-1 bg-border" />
            </div>
          ) : (
            <div
              className="flex items-center pt-1 font-system-ui"
              style={{ fontSize: `${shared.appTypographyScale.uiSmPx}px` }}
            >
              {/* The sweep of light is the liveness cue, so no pulsing dots follow it. */}
              <span className="working-shimmer">
                {row.createdAt ? (
                  <>
                    Trabajando (
                    {shared.nowIso ? (
                      (formatWorkingTimer(row.createdAt, shared.nowIso) ?? "0s")
                    ) : (
                      <WorkingTimer createdAt={row.createdAt} />
                    )}
                    )...
                  </>
                ) : (
                  "Trabajando..."
                )}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

type TimelineMessage = Extract<MessagesTimelineRow, { kind: "message" }>["message"];
type TimelineProposedPlan = Extract<MessagesTimelineRow, { kind: "proposed-plan" }>["proposedPlan"];
type TimelineWorkEntry = Extract<MessagesTimelineRow, { kind: "work" }>["groupedEntries"][number];

interface PinnedUserMessageRow {
  rowIndex: number;
  messageId: MessageId;
  message: TimelineMessage;
}

/** Everything a single evaluation pass needs, read once per frame. */
interface PinnedUserMessageGeometry {
  /** Top edge of the scroll viewport, in client coordinates. */
  containerTopPx: number;
  /** Top edge of the virtualized spacer, relative to the scroll viewport. */
  spacerTopPx: number | null;
  measurements: ReadonlyArray<VirtualItem>;
}

/**
 * Tracks which user message should be shown pinned at the top of the transcript.
 *
 * Row tops come from the virtualizer's measurement cache rather than the DOM,
 * because the row we want to pin has by definition scrolled past the top edge
 * and is usually unmounted. The unvirtualized tail is the exception: those rows
 * never enter the virtualizer's cache, so they are measured from their (always
 * mounted) elements instead.
 *
 * The scroll listener is a passive one of our own so the auto-scroll controller's
 * handlers on the same element keep working untouched.
 */
function usePinnedUserMessage(input: {
  scrollContainer: HTMLDivElement | null;
  userMessageRows: ReadonlyArray<PinnedUserMessageRow>;
  virtualizedRowCount: number;
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
  virtualSpacerRef: RefObject<HTMLDivElement | null>;
  tailRowElementsRef: RefObject<Map<number, HTMLElement>>;
  /** Height of the rendered pin, so a jump can clear it. */
  pinnedElementRef: RefObject<HTMLDivElement | null>;
  /**
   * Not read, only depended on: a width change or a new chat font size reflows
   * every row, and a reflow that happens to leave the total size untouched
   * would otherwise go unnoticed until the next scroll.
   */
  layoutRevision: string;
}): {
  pinnedRow: PinnedUserMessageRow | null;
  scrollToPinnedRow: () => void;
  scrollbarWidthPx: number;
} {
  const {
    scrollContainer,
    userMessageRows,
    virtualizedRowCount,
    rowVirtualizer,
    virtualSpacerRef,
    tailRowElementsRef,
    pinnedElementRef,
    layoutRevision,
  } = input;

  const [pinnedRowIndex, setPinnedRowIndex] = useState<number | null>(null);
  const pinnedRowIndexRef = useRef<number | null>(null);
  const [frameScheduler] = useState(() => createFrameScheduler(window));
  // The pin is positioned against the pane, which is wider than the scrolled
  // content by exactly the scrollbar. Without this it would sit a few pixels to
  // the right of the bubbles on every platform that draws a classic scrollbar.
  const [scrollbarWidthPx, setScrollbarWidthPx] = useState(0);
  const scrollbarWidthRef = useRef(0);

  const readGeometry = useCallback((): PinnedUserMessageGeometry | null => {
    if (!scrollContainer) return null;
    const containerTopPx = scrollContainer.getBoundingClientRect().top;
    const spacer = virtualSpacerRef.current;
    return {
      containerTopPx,
      spacerTopPx: spacer ? spacer.getBoundingClientRect().top - containerTopPx : null,
      // `measurementsCache` is the same array `getVirtualItems()` derives the
      // rendered rows from, and it covers every index — including the ones
      // currently unmounted, which is the whole point of pinning.
      measurements: virtualizedRowCount > 0 ? rowVirtualizer.measurementsCache : [],
    };
  }, [rowVirtualizer, scrollContainer, virtualSpacerRef, virtualizedRowCount]);

  const measureRowViewportTopPx = useCallback(
    (rowIndex: number, geometry: PinnedUserMessageGeometry): number | null => {
      if (rowIndex >= virtualizedRowCount) {
        const element = tailRowElementsRef.current.get(rowIndex);
        if (!element) return null;
        return element.getBoundingClientRect().top - geometry.containerTopPx;
      }
      if (geometry.spacerTopPx === null) return null;
      const measurement = geometry.measurements[rowIndex];
      if (!measurement) return null;
      return geometry.spacerTopPx + measurement.start;
    },
    [tailRowElementsRef, virtualizedRowCount],
  );

  const evaluate = useCallback(() => {
    if (scrollContainer) {
      const nextScrollbarWidthPx = scrollContainer.offsetWidth - scrollContainer.clientWidth;
      if (scrollbarWidthRef.current !== nextScrollbarWidthPx) {
        scrollbarWidthRef.current = nextScrollbarWidthPx;
        setScrollbarWidthPx(nextScrollbarWidthPx);
      }
    }

    const geometry = readGeometry();
    let nextPinnedRowIndex: number | null = null;

    if (geometry && userMessageRows.length > 0) {
      const candidates: PinnedUserMessageCandidate[] = [];
      for (const row of userMessageRows) {
        const viewportTopPx = measureRowViewportTopPx(row.rowIndex, geometry);
        if (viewportTopPx === null) continue;
        candidates.push({ rowIndex: row.rowIndex, viewportTopPx });
      }
      nextPinnedRowIndex = resolvePinnedUserMessageIndex(candidates);
    }

    if (pinnedRowIndexRef.current === nextPinnedRowIndex) return;
    pinnedRowIndexRef.current = nextPinnedRowIndex;
    setPinnedRowIndex(nextPinnedRowIndex);
  }, [measureRowViewportTopPx, readGeometry, scrollContainer, userMessageRows]);

  // The queued frame must run whatever `evaluate` looks like when it fires, not
  // the one captured when it was queued. Otherwise a frame scheduled before the
  // scroll container was attached — or while the tab was in the background and
  // rAF was not running at all — keeps the slot busy and answers with stale
  // geometry, and every later schedule short-circuits behind it.
  const evaluateRef = useRef(evaluate);
  useEffect(() => {
    evaluateRef.current = evaluate;
  });

  const scheduleEvaluate = useCallback(() => {
    frameScheduler.schedule(() => {
      evaluateRef.current();
    });
  }, [frameScheduler]);

  useEffect(() => {
    if (!scrollContainer) return;
    scrollContainer.addEventListener("scroll", scheduleEvaluate, { passive: true });
    return () => {
      scrollContainer.removeEventListener("scroll", scheduleEvaluate);
    };
  }, [scheduleEvaluate, scrollContainer]);

  // Re-run when the content itself moves: new rows, rows leaving the tail, and
  // late measurements all shift the offsets we just read.
  const totalSizePx = rowVirtualizer.getTotalSize();
  useEffect(() => {
    scheduleEvaluate();
  }, [layoutRevision, scheduleEvaluate, totalSizePx, userMessageRows, virtualizedRowCount]);

  useEffect(() => {
    return () => {
      frameScheduler.cancel();
    };
  }, [frameScheduler]);

  const scrollToPinnedRow = useCallback(() => {
    const rowIndex = pinnedRowIndexRef.current;
    if (rowIndex === null || !scrollContainer) return;
    const geometry = readGeometry();
    if (!geometry) return;
    const viewportTopPx = measureRowViewportTopPx(rowIndex, geometry);
    if (viewportTopPx === null) return;

    const clearancePx =
      (pinnedElementRef.current?.getBoundingClientRect().height ?? 0) +
      PINNED_USER_MESSAGE_JUMP_GAP_PX;
    scrollContainer.scrollTop += viewportTopPx - clearancePx;
  }, [measureRowViewportTopPx, pinnedElementRef, readGeometry, scrollContainer]);

  const pinnedRow =
    pinnedRowIndex === null
      ? null
      : (userMessageRows.find((row) => row.rowIndex === pinnedRowIndex) ?? null);

  return { pinnedRow, scrollToPinnedRow, scrollbarWidthPx };
}

/**
 * Ask for older history near the top of the scroll, and keep the user's place
 * when it arrives.
 *
 * One hook for both halves because they share the same piece of state: the id
 * of the row that used to be first. The request needs it to know it has already
 * been served; the compensation needs it to measure how much content appeared
 * above the viewport.
 *
 * The compensation exists because TanStack Virtual anchors nothing at the top —
 * prepending rows shifts every offset down and the viewport stays where it was,
 * which reads as the transcript jumping forward by a page. Adjusting `scrollTop`
 * by the height that appeared above keeps the same row under the cursor.
 *
 * The height comes from the virtualizer's own measurements rather than from
 * `scrollHeight`, because the tail can grow in the very same commit (a streaming
 * assistant message) and a raw height delta would mistake that for content
 * added at the top. `scrollHeight` is only the fallback for the case where the
 * whole transcript is short enough to be unvirtualized.
 *
 * Newly prepended rows arrive with estimated heights and settle a frame later;
 * the virtualizer's own `shouldAdjustScrollPositionOnItemSizeChange` already
 * compensates for those late measurements while the viewport is away from the
 * bottom, which is exactly the situation here.
 */
function useOlderHistoryLoader(input: {
  scrollContainer: HTMLDivElement | null;
  rows: ReadonlyArray<MessagesTimelineRow>;
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
  hasMoreHistory: boolean;
  isLoadingOlderHistory: boolean;
  onLoadOlderHistory: (() => void) | undefined;
}): void {
  const {
    scrollContainer,
    rows,
    rowVirtualizer,
    hasMoreHistory,
    isLoadingOlderHistory,
    onLoadOlderHistory,
  } = input;

  const firstRowId = rows[0]?.id ?? null;
  const previousFirstRowIdRef = useRef<string | null>(firstRowId);
  const previousScrollHeightRef = useRef(0);

  // Read through refs so the scroll listener is installed once per container
  // instead of being torn down and re-added on every render of the transcript.
  const canRequest = hasMoreHistory && !isLoadingOlderHistory && onLoadOlderHistory !== undefined;
  const canRequestRef = useRef(canRequest);
  const requestRef = useRef(onLoadOlderHistory);
  useEffect(() => {
    canRequestRef.current = canRequest;
    requestRef.current = onLoadOlderHistory;
  });

  useEffect(() => {
    if (!scrollContainer) return;
    const maybeRequestOlderHistory = () => {
      if (!canRequestRef.current) return;
      if (scrollContainer.scrollTop > LOAD_OLDER_HISTORY_THRESHOLD_PX) return;
      requestRef.current?.();
    };
    // Deliberately not fired once on mount: a transcript that has not been
    // auto-scrolled to the bottom yet sits at `scrollTop === 0`, and firing
    // there would load a page nobody asked for on every thread open.
    scrollContainer.addEventListener("scroll", maybeRequestOlderHistory, { passive: true });
    return () => {
      scrollContainer.removeEventListener("scroll", maybeRequestOlderHistory);
    };
  }, [scrollContainer]);

  useLayoutEffect(() => {
    const previousFirstRowId = previousFirstRowIdRef.current;
    const previousScrollHeight = previousScrollHeightRef.current;
    previousFirstRowIdRef.current = firstRowId;

    if (!scrollContainer) return;
    if (previousFirstRowId === null || previousFirstRowId === firstRowId) {
      previousScrollHeightRef.current = scrollContainer.scrollHeight;
      return;
    }
    // A changed first row is not necessarily a prepend: switching threads,
    // reverting a checkpoint or crossing a fresh compaction cut-off all replace
    // the head. Only treat it as one when the old first row is still present
    // and has moved down.
    const previousFirstIndex = rows.findIndex((row) => row.id === previousFirstRowId);
    if (previousFirstIndex <= 0) {
      previousScrollHeightRef.current = scrollContainer.scrollHeight;
      return;
    }

    // Same array the pin reads offsets from: the public measurement cache, not
    // the visible window, so a row far above the viewport still has a position.
    const measurement = rowVirtualizer.measurementsCache[previousFirstIndex];
    const addedAbovePx =
      measurement !== undefined && measurement.key === previousFirstRowId
        ? measurement.start
        : scrollContainer.scrollHeight - previousScrollHeight;
    if (addedAbovePx > 0) {
      scrollContainer.scrollTop += addedAbovePx;
    }
    previousScrollHeightRef.current = scrollContainer.scrollHeight;
  }, [firstRowId, rowVirtualizer, rows, scrollContainer]);
}

// Reuse stable row references so streaming updates only force React work for
// rows whose visible content actually changed.
function useStableRows(rows: MessagesTimelineRow[]): MessagesTimelineRow[] {
  const previousStateRef = useRef<StableMessagesTimelineRowsState>({
    byId: new Map<string, MessagesTimelineRow>(),
    result: [],
  });

  return useMemo(() => {
    const nextState = computeStableMessagesTimelineRows(rows, previousStateRef.current);
    previousStateRef.current = nextState;
    return nextState.result;
  }, [rows]);
}

function estimateTimelineProposedPlanHeight(
  proposedPlan: TimelineProposedPlan,
  chatFontSizePx: number,
): number {
  const estimatedLines = Math.max(1, Math.ceil(proposedPlan.planMarkdown.length / 72));
  return 120 + Math.min(estimatedLines * getChatTranscriptLineHeightPx(chatFontSizePx), 880);
}

function LiveMessageMeta({
  createdAt,
  durationStart,
  timestampFormat,
}: {
  createdAt: string;
  durationStart: string;
  timestampFormat: TimestampFormat;
}) {
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => {
      setNowMs(Date.now());
    }, 1000);
    return () => {
      window.clearInterval(id);
    };
  }, [durationStart]);

  return (
    <>
      {formatMessageMeta(
        createdAt,
        formatElapsed(durationStart, new Date(nowMs).toISOString()),
        timestampFormat,
      )}
    </>
  );
}

function formatWorkingLabel(label: string): string {
  const normalized = normalizeCompactToolLabel(label).trim();
  if (normalized === "Compacting context") {
    return "Compacting conversation...";
  }
  // A compaction that already finished says so; appending an ellipsis would
  // turn a settled fact back into an announcement of ongoing work.
  if (normalized === "Context compacted") {
    return normalized;
  }
  return normalized.endsWith("...") ? normalized : `${normalized}...`;
}

function formatMessageMeta(
  createdAt: string,
  duration: string | null,
  timestampFormat: TimestampFormat,
): string {
  if (!duration) return formatShortTimestamp(createdAt, timestampFormat);
  return `${formatShortTimestamp(createdAt, timestampFormat)} • ${duration}`;
}

function formatInlineWorkSummary(_groupedEntries: TimelineWorkEntry[]): string | null {
  return null;
}

/**
 * A notice the server wrote, not the user.
 *
 * Deliberately quiet and not a bubble: it explains why a turn started rather
 * than being part of the conversation. Collapsed to its headline, since the body
 * is instructions aimed at the agent, not at the reader.
 */
const SystemNoticeRow = memo(function SystemNoticeRow({
  text,
  fontSizePx,
  createdAt,
  timestampFormat,
}: {
  text: string;
  fontSizePx: number;
  createdAt: string;
  timestampFormat: TimestampFormat;
}) {
  const [expanded, setExpanded] = useState(false);
  const notice = useMemo(() => describeSystemNotice(text), [text]);

  return (
    <div className="my-1.5 flex w-full min-w-0 items-start gap-2 px-1">
      <BotIcon
        aria-hidden="true"
        className="mt-px size-3.5 shrink-0 text-violet-600/80 dark:text-violet-300/70"
      />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-2">
          <span className="text-muted-foreground/85" style={{ fontSize: `${fontSizePx}px` }}>
            {notice.headline}
          </span>
          <span className="text-muted-foreground/45" style={{ fontSize: `${fontSizePx}px` }}>
            {formatMessageMeta(createdAt, null, timestampFormat)}
          </span>
          {notice.details ? (
            <button
              type="button"
              className="text-muted-foreground/55 underline-offset-2 transition-colors hover:text-foreground/80 hover:underline"
              style={{ fontSize: `${fontSizePx}px` }}
              onClick={() => setExpanded((previous) => !previous)}
            >
              {expanded ? "Ocultar detalle" : "Ver detalle"}
            </button>
          ) : null}
        </div>
        {expanded && notice.details ? (
          <pre
            className="whitespace-pre-wrap break-words font-sans text-muted-foreground/70"
            style={{ fontSize: `${fontSizePx}px` }}
          >
            {notice.details}
          </pre>
        ) : null}
      </div>
    </div>
  );
});

const UserMessageTerminalContextInlineLabel = memo(
  function UserMessageTerminalContextInlineLabel(props: { context: ParsedQuotedContextEntry }) {
    const tooltipText =
      props.context.body.length > 0
        ? `${props.context.header}\n${props.context.body}`
        : props.context.header;

    return (
      <QuotedContextInlineChip
        kind={inferQuotedContextKindFromHeader(props.context.header)}
        label={props.context.header}
        tooltipText={tooltipText}
      />
    );
  },
);

const UserMessageInlineSkillChip = memo(function UserMessageInlineSkillChip(props: {
  skillName: string;
}) {
  return (
    <span className={COMPOSER_INLINE_SKILL_CHIP_CLASS_NAME}>
      <span
        aria-hidden="true"
        className={COMPOSER_INLINE_SKILL_CHIP_ICON_CLASS_NAME}
        dangerouslySetInnerHTML={{ __html: COMPOSER_INLINE_SKILL_CHIP_ICON_SVG }}
      />
      <span className={COMPOSER_INLINE_CHIP_LABEL_CLASS_NAME}>
        {formatComposerSkillChipLabel(props.skillName)}
      </span>
    </span>
  );
});

/**
 * A non-image attachment in the transcript: name, size, and a download.
 *
 * A link rather than a preview button because the server refuses to serve
 * these inline — `/attachments/<id>` answers with `Content-Disposition:
 * attachment` — so "open" and "download" are the same action, and pretending
 * otherwise would mean a click that appears to do nothing.
 */
const UserFileAttachmentChip = memo(function UserFileAttachmentChip(props: {
  attachment: NonNullable<TimelineMessage["attachments"]>[number];
  resolvedTheme: "light" | "dark";
}) {
  // Always the download URL, never the stored `previewUrl`: the latter is the
  // bare route, and the name query is what makes the saved file keep its name
  // when the page and the server are different origins.
  const href = resolveAttachmentDownloadUrl(props.attachment.id, props.attachment.name);
  return (
    <a
      href={href}
      download={props.attachment.name}
      className="inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-xl border border-border/70 bg-background/82 px-2 py-1.5 text-left transition-colors hover:bg-background/94"
      title={`${props.attachment.name} · ${formatAttachmentSize(props.attachment.sizeBytes)}`}
    >
      <VscodeEntryIcon
        pathValue={props.attachment.name}
        kind="file"
        theme={props.resolvedTheme}
        className="size-4 shrink-0"
      />
      <span className="min-w-0 truncate text-[12px] font-medium text-foreground/84">
        {props.attachment.name}
      </span>
      <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground/70">
        {formatAttachmentSize(props.attachment.sizeBytes)}
      </span>
    </a>
  );
});

const UserImageAttachmentThumbnail = memo(function UserImageAttachmentThumbnail(props: {
  image: NonNullable<TimelineMessage["attachments"]>[number];
  userImages: NonNullable<TimelineMessage["attachments"]>;
  onImageExpand: (preview: ExpandedImagePreview) => void;
  onTimelineImageLoad: () => void;
  resolvedTheme: "light" | "dark";
}) {
  return (
    <button
      type="button"
      className="flex size-15 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-border/70 bg-background/82 text-left shadow-[0_1px_0_rgba(255,255,255,0.2)_inset] transition-colors hover:bg-background/94"
      aria-label={`Preview ${props.image.name}`}
      title={props.image.name}
      onClick={() => {
        const preview = buildExpandedImagePreview(props.userImages, props.image.id);
        if (!preview) return;
        props.onImageExpand(preview);
      }}
    >
      {props.image.previewUrl ? (
        <img
          src={props.image.previewUrl}
          alt={props.image.name}
          className="size-full object-cover"
          onLoad={props.onTimelineImageLoad}
          onError={props.onTimelineImageLoad}
        />
      ) : (
        <div className="flex size-full items-center justify-center">
          <VscodeEntryIcon
            pathValue={props.image.name}
            kind="file"
            theme={props.resolvedTheme}
            className="size-4 opacity-70"
          />
        </div>
      )}
    </button>
  );
});

// Renders read-only user text with the same inline skill pill treatment as the composer.
function renderUserMessageInlineText(text: string, keyPrefix: string): ReactNode[] {
  return splitPromptIntoDisplaySegments(text).flatMap((segment, index) => {
    const key = `${keyPrefix}:${index}`;
    if (segment.type === "text") {
      return segment.text.length > 0 ? [<span key={`${key}:text`}>{segment.text}</span>] : [];
    }
    if (segment.type === "skill") {
      return [<UserMessageInlineSkillChip key={`${key}:skill`} skillName={segment.name} />];
    }
    if (segment.type === "mention") {
      return [<span key={`${key}:mention`}>{`@${segment.path}`}</span>];
    }
    if (segment.type === "agent-mention") {
      return [
        <UserMessageInlineAgentChip
          key={`${key}:agent`}
          alias={segment.alias}
          color={segment.color}
        />,
      ];
    }
    return [];
  });
}

function hasOnlyInlineSkillChips(text: string): boolean {
  const segments = splitPromptIntoDisplaySegments(text);
  let skillCount = 0;

  for (const segment of segments) {
    if (segment.type === "skill") {
      skillCount += 1;
      continue;
    }
    if (segment.type === "text" && segment.text.trim().length === 0) {
      continue;
    }
    return false;
  }

  return skillCount > 0;
}

const UserMessageBody = memo(function UserMessageBody(props: {
  text: string;
  quotedContexts: ParsedQuotedContextEntry[];
  chatTypographyStyle: CSSProperties;
}) {
  if (props.quotedContexts.length > 0) {
    const hasEmbeddedInlineLabels = textContainsInlineTerminalContextLabels(
      props.text,
      props.quotedContexts,
    );
    const inlinePrefix = buildInlineTerminalContextText(props.quotedContexts);
    const inlineNodes: ReactNode[] = [];

    if (hasEmbeddedInlineLabels) {
      let cursor = 0;

      for (const context of props.quotedContexts) {
        const label = formatInlineQuotedContextLabelFromHeader(context.header);
        const matchIndex = props.text.indexOf(label, cursor);
        if (matchIndex === -1) {
          inlineNodes.length = 0;
          break;
        }
        if (matchIndex > cursor) {
          inlineNodes.push(
            ...renderUserMessageInlineText(
              props.text.slice(cursor, matchIndex),
              `user-terminal-context-inline-before:${context.header}:${cursor}`,
            ),
          );
        }
        inlineNodes.push(
          <UserMessageTerminalContextInlineLabel
            key={`user-terminal-context-inline:${context.header}`}
            context={context}
          />,
        );
        cursor = matchIndex + label.length;
      }

      if (inlineNodes.length > 0) {
        if (cursor < props.text.length) {
          inlineNodes.push(
            ...renderUserMessageInlineText(
              props.text.slice(cursor),
              `user-message-terminal-context-inline-rest:${cursor}`,
            ),
          );
        }

        return (
          <div
            className="inline-block max-w-full min-w-0 wrap-break-word whitespace-pre-wrap font-system-ui text-foreground"
            style={props.chatTypographyStyle}
          >
            {inlineNodes}
          </div>
        );
      }
    }

    for (const context of props.quotedContexts) {
      inlineNodes.push(
        <UserMessageTerminalContextInlineLabel
          key={`user-terminal-context-inline:${context.header}`}
          context={context}
        />,
      );
      inlineNodes.push(
        <span key={`user-terminal-context-inline-space:${context.header}`} aria-hidden="true">
          {" "}
        </span>,
      );
    }

    if (props.text.length > 0) {
      inlineNodes.push(
        ...renderUserMessageInlineText(props.text, "user-message-terminal-context-inline-text"),
      );
    } else if (inlinePrefix.length === 0) {
      return null;
    }

    return (
      <div
        className="inline-block max-w-full min-w-0 wrap-break-word whitespace-pre-wrap font-system-ui text-foreground"
        style={props.chatTypographyStyle}
      >
        {inlineNodes}
      </div>
    );
  }

  if (props.text.length === 0) {
    return null;
  }

  if (props.quotedContexts.length === 0 && hasOnlyInlineSkillChips(props.text)) {
    return (
      <div
        className="flex max-w-full min-w-0 items-center leading-none text-foreground [&>span]:translate-y-0"
        style={props.chatTypographyStyle}
      >
        {renderUserMessageInlineText(props.text, "user-message-inline-chip-only")}
      </div>
    );
  }

  return (
    <div
      className="inline-block max-w-full min-w-0 whitespace-pre-wrap break-words font-system-ui text-foreground"
      style={props.chatTypographyStyle}
    >
      {renderUserMessageInlineText(props.text, "user-message-inline")}
    </div>
  );
});

const UserMessageInlineAgentChip = memo(function UserMessageInlineAgentChip(props: {
  alias: string;
  color: string;
}) {
  const colors = AGENT_COLOR_STYLES[props.color] ?? DEFAULT_AGENT_COLOR;

  return (
    <span
      className={COMPOSER_INLINE_AGENT_CHIP_CLASS_NAME}
      style={{
        backgroundColor: colors.bg,
        color: colors.text,
      }}
    >
      <RiRobot3Line className={COMPOSER_INLINE_AGENT_CHIP_ICON_CLASS_NAME} />
      <span className={COMPOSER_INLINE_CHIP_LABEL_CLASS_NAME}>{`@${props.alias}`}</span>
    </span>
  );
});

function workToneIcon(tone: TimelineWorkEntry["tone"]): {
  icon: LucideIcon;
  className: string;
} {
  if (tone === "error") {
    return {
      icon: CircleAlertIcon,
      className: "text-muted-foreground/50",
    };
  }
  if (tone === "thinking") {
    return {
      icon: BotIcon,
      className: "text-muted-foreground/40",
    };
  }
  if (tone === "info") {
    return {
      icon: CheckIcon,
      className: "text-muted-foreground/50",
    };
  }
  return {
    icon: ZapIcon,
    className: "text-muted-foreground/45",
  };
}

function workToneClass(tone: "thinking" | "tool" | "info" | "error"): string {
  if (tone === "error") return "text-rose-400/70 dark:text-rose-300/70";
  if (tone === "tool") return "text-muted-foreground/80";
  if (tone === "thinking") return "text-muted-foreground/60";
  return "text-muted-foreground/55";
}

/**
 * Try to extract a clean file path from a detail string that may contain JSON.
 * Handles patterns like:
 *   Read {"file_path":"/Users/foo/bar.ts","offset":10}
 *   {"file_path":"/path/to/file.ts"}
 */
function extractFilePathFromDetail(detail: string): string | null {
  // Try to find a JSON-like object in the detail
  const jsonStart = detail.indexOf("{");
  if (jsonStart < 0) return null;
  const jsonEnd = detail.lastIndexOf("}");
  if (jsonEnd <= jsonStart) return null;
  try {
    const parsed = JSON.parse(detail.slice(jsonStart, jsonEnd + 1));
    const filePath = parsed.file_path ?? parsed.filePath ?? parsed.path ?? parsed.filename ?? null;
    if (typeof filePath === "string" && filePath.trim().length > 0) {
      return filePath.trim();
    }
  } catch {
    // Not valid JSON — try regex fallback
    const match = /"(?:file_path|filePath|path|filename)"\s*:\s*"([^"]+)"/i.exec(detail);
    if (match?.[1]) return match[1];
  }
  return null;
}

function workEntryPreview(
  workEntry: Pick<
    TimelineWorkEntry,
    "detail" | "command" | "changedFiles" | "requestKind" | "itemType" | "subagentTask"
  >,
): string | null {
  const isFileRelated =
    workEntry.requestKind === "file-read" ||
    workEntry.requestKind === "file-change" ||
    workEntry.itemType === "file_change";

  // Prefer clean basenames from changedFiles
  if (workEntry.changedFiles && workEntry.changedFiles.length > 0) {
    const names = workEntry.changedFiles.map((p) => basename(p));
    if (names.length === 1) return names[0]!;
    return `${names.length} files`;
  }

  // For commands, show the command itself
  if (workEntry.command) return workEntry.command;

  // A running subagent says what it is doing right now; once it is done the
  // heading already names it, so the preview goes quiet instead of repeating.
  if (workEntry.subagentTask) {
    return workEntry.subagentTask.status === "running"
      ? runningSubagentTaskPreview(workEntry.subagentTask)
      : null;
  }

  // For detail, try to extract a clean file path first
  if (workEntry.detail) {
    const filePath = extractFilePathFromDetail(workEntry.detail);
    if (filePath) return basename(filePath);

    // For file-related entries, the heading alone is enough — don't show raw JSON
    if (isFileRelated) return null;

    // For other entries, if the detail looks like raw JSON, skip it
    const trimmedDetail = workEntry.detail.trim();
    if (trimmedDetail.startsWith("{") || trimmedDetail.startsWith("[")) return null;

    // Clean, non-JSON detail — show it
    return trimmedDetail;
  }

  return null;
}

/**
 * What a running subagent row trails after its heading. Claude reports the tool
 * its subagent is running; Codex reports none, so the row says how many agents
 * are out there instead of inventing an action.
 */
function runningSubagentTaskPreview(
  subagentTask: NonNullable<TimelineWorkEntry["subagentTask"]>,
): string {
  if (subagentTask.currentAction) {
    return `running ${subagentTask.currentAction}…`;
  }
  const receiverCount = subagentTask.receiverProviderThreadIds?.length ?? 0;
  if (receiverCount > 0) {
    return receiverCount === 1 ? "running 1 subagent…" : `running ${receiverCount} subagents…`;
  }
  return "running…";
}

function workEntryIcon(workEntry: TimelineWorkEntry): LucideIcon {
  if (workEntry.boundaryKind === "clear") return EraserIcon;
  // A failed compaction falls through to the generic tone-driven icon below
  // (CircleAlertIcon for tone "error") instead of the neutral compaction
  // glyph, so the row still reads as an error at a glance.
  if (workEntry.boundaryKind === "compaction" && workEntry.tone !== "error") {
    return ArrowsMinimizeIcon;
  }
  if (workEntry.memoryKind !== undefined) return BookIcon;
  if (workEntry.requestKind === "command") return TerminalIcon;
  if (workEntry.requestKind === "file-read") return EyeIcon;
  if (workEntry.requestKind === "file-change") return SquarePenIcon;

  if (workEntry.itemType === "command_execution" || workEntry.command) {
    return TerminalIcon;
  }
  if (workEntry.itemType === "file_change" || (workEntry.changedFiles?.length ?? 0) > 0) {
    return SquarePenIcon;
  }
  if (workEntry.itemType === "web_search") return GlobeIcon;
  if (workEntry.itemType === "image_view") return EyeIcon;

  switch (workEntry.itemType) {
    case "mcp_tool_call":
      return SkillCubeIcon;
    case "dynamic_tool_call":
      return HammerIcon;
    case "collab_agent_tool_call":
      return AgentTaskIcon;
  }

  return workToneIcon(workEntry.tone).icon;
}

// Keep command, agent-task, and file-change rows visually compact so their icon can trail the label.
function prefersCompactWorkEntryRow(workEntry: TimelineWorkEntry): boolean {
  const EntryIcon = workEntryIcon(workEntry);
  return (
    EntryIcon === TerminalIcon ||
    EntryIcon === HammerIcon ||
    EntryIcon === AgentTaskIcon ||
    EntryIcon === SquarePenIcon
  );
}

function capitalizePhrase(value: string): string {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return value;
  }
  return `${trimmed.charAt(0).toUpperCase()}${trimmed.slice(1)}`;
}

function toolWorkEntryHeading(workEntry: TimelineWorkEntry): string {
  const heading = workEntry.toolTitle
    ? capitalizePhrase(normalizeCompactToolLabel(workEntry.toolTitle))
    : capitalizePhrase(normalizeCompactToolLabel(workEntry.label));
  // Repeated identical rows are folded into one; say how many there were so the
  // count is not silently lost.
  return workEntry.count !== undefined && workEntry.count > 1
    ? `${heading} ×${workEntry.count}`
    : heading;
}

/**
 * The row's headline for a compaction, derived from the structured status
 * rather than the server's free-text `label` — so the row reads the same
 * regardless of which provider (or which point in the cycle) produced it.
 */
function compactionHeading(compaction: NonNullable<TimelineWorkEntry["compaction"]>): string {
  switch (compaction.status) {
    case "inProgress":
      return "Compacting conversation…";
    case "failed":
      return "Context compaction failed";
    case "completed":
    default:
      return "Conversation compacted";
  }
}

/**
 * The tenuous "~186k tokens saved" trailer next to a completed compaction's
 * heading. Only shown when the provider reported both sides of the trade —
 * a compaction missing either number stays silent rather than guessing.
 */
function compactionSavedSuffix(
  compaction: NonNullable<TimelineWorkEntry["compaction"]>,
): string | null {
  if (compaction.status !== "completed") {
    return null;
  }
  if (compaction.preTokens === undefined || compaction.postTokens === undefined) {
    return null;
  }
  const saved = compaction.preTokens - compaction.postTokens;
  // An anomalous provider report (post >= pre) has nothing real to brag about —
  // stay silent rather than print "~0 tokens saved" or a negative number.
  if (saved <= 0) {
    return null;
  }
  return `· ~${formatContextWindowTokens(saved)} tokens saved`;
}

/**
 * The expanded panel's right-aligned meta line: whichever of trigger and the
 * pre → post token counts the provider actually reported, joined with a
 * middot. Either half can be missing (Codex omits trigger on auto-compaction;
 * a provider may never report tokens), so this degrades to just one part, or
 * `null` when neither is known.
 */
function compactionDetailMeta(
  compaction: NonNullable<TimelineWorkEntry["compaction"]>,
): string | null {
  const parts: string[] = [];
  if (compaction.trigger) {
    parts.push(compaction.trigger);
  }
  if (compaction.preTokens !== undefined && compaction.postTokens !== undefined) {
    parts.push(
      `${formatContextWindowTokens(compaction.preTokens)} → ${formatContextWindowTokens(compaction.postTokens)} tokens`,
    );
  }
  return parts.length > 0 ? parts.join(" · ") : null;
}

function isFileChangeWorkEntry(workEntry: TimelineWorkEntry): boolean {
  return (
    workEntry.requestKind === "file-change" ||
    workEntry.itemType === "file_change" ||
    (workEntry.changedFiles?.length ?? 0) > 0
  );
}

// Level 1: the whole run behind a single line. Reads as a sentence about what the
// agent did, so it stays useful even when every row underneath is hidden.
function workGroupSummary(entries: ReadonlyArray<TimelineWorkEntry>): {
  label: string;
  Icon: LucideIcon;
} {
  const count = entries.length;
  const onlyCommands = entries.every(
    (entry) =>
      entry.itemType === "command_execution" ||
      entry.requestKind === "command" ||
      Boolean(entry.command),
  );
  if (onlyCommands) {
    return {
      label: count === 1 ? "Ran 1 command" : `Ran ${count} commands`,
      Icon: TerminalIcon,
    };
  }
  // Memory reads earn a mention in the collapsed line, alone or alongside other
  // tools, because they are the one row that explains the *rest* of the turn:
  // an agent that went straight to the right procedure and one that guessed
  // produce the same "Used 4 tools", and the difference is the whole point of
  // having memories at all. Everything else is legible once expanded; this has
  // to be legible before.
  const recalled = entries.filter((entry) => entry.memoryKind === "recalled").length;
  const proposed = entries.filter((entry) => entry.memoryKind === "proposed").length;
  if (recalled > 0 || proposed > 0) {
    // Kept apart because they are opposite acts: one is the agent applying what
    // this installation already knows, the other is it asking to add to that.
    // Collapsing both into "memory" would hide which one just happened.
    const parts: string[] = [];
    if (recalled > 0) {
      parts.push(recalled === 1 ? "recalled 1 memory" : `recalled ${recalled} memories`);
    }
    if (proposed > 0) {
      parts.push(proposed === 1 ? "proposed 1 memory" : `proposed ${proposed} memories`);
    }
    const memoryPhrase = parts.join(" · ");
    const others = count - recalled - proposed;
    return {
      label:
        others === 0
          ? memoryPhrase.charAt(0).toUpperCase() + memoryPhrase.slice(1)
          : `Used ${others === 1 ? "1 tool" : `${others} tools`} · ${memoryPhrase}`,
      Icon: BookIcon,
    };
  }
  return {
    label: count === 1 ? "Used 1 tool" : `Used ${count} tools`,
    Icon: HammerIcon,
  };
}

/**
 * Height budget for a folded turn, so the virtualizer places the rows below it
 * without a visible settle. One line of `uiSm` text, the row's own padding and
 * the rule underneath it.
 */
const TURN_FOLD_ROW_HEIGHT_PX = 33;

/**
 * The one row a settled turn leaves behind: how long the agent worked, and the
 * way back into everything it did. Collapsed by default — the reasoning and the
 * tool traffic are how the answer was reached, not the answer.
 *
 * The hairline underneath is what makes the row readable at a glance: it draws
 * the line the answer starts on, so a long transcript scans as a stack of
 * answers rather than a run-on column of text.
 */
function TurnFoldRow(props: {
  label: string;
  expanded: boolean;
  fontSizePx: number;
  onToggle: () => void;
}) {
  const { label, expanded, fontSizePx, onToggle } = props;

  return (
    <div
      // `--border` is only 4% white in dark, so an opacity suffix on top of it
      // leaves a rule that is barely there. Mixing toward the foreground is how
      // the rest of the app lifts a hairline (see `.chat-markdown-copy-button`).
      className="border-b border-[color-mix(in_srgb,var(--border)_90%,var(--foreground))]/90 pb-2 pt-1"
    >
      <button
        type="button"
        aria-expanded={expanded}
        // Names this button as the turn's fold toggle, the way
        // `data-work-group-summary` names a work group's.
        data-turn-fold-summary="true"
        onClick={onToggle}
        className="flex items-center gap-1.5 py-0.5 text-left tabular-nums text-muted-foreground/60 transition-colors duration-150 hover:text-muted-foreground"
      >
        <DisclosureChevron open={expanded} className="size-3 text-muted-foreground/40" />
        <span className="truncate" style={{ fontSize: `${fontSizePx}px` }}>
          {label}
        </span>
      </button>
    </div>
  );
}

function WorkGroupSummaryHeader(props: {
  entries: ReadonlyArray<TimelineWorkEntry>;
  expanded: boolean;
  fontSizePx: number;
  onToggle: () => void;
}) {
  const { entries, expanded, fontSizePx, onToggle } = props;
  const { label, Icon } = workGroupSummary(entries);

  return (
    <button
      type="button"
      aria-expanded={expanded}
      // Names this button as the group's fold toggle, the way
      // `data-tool-detail-panel` names a row's detail: several buttons in a
      // timeline carry `aria-expanded`, and telling them apart otherwise means
      // matching on class names.
      data-work-group-summary="true"
      onClick={onToggle}
      className="flex w-full items-center gap-1.5 py-0.5 text-left text-muted-foreground/45 transition-colors duration-150 hover:text-muted-foreground/75"
    >
      <DisclosureChevron open={expanded} className="size-3 text-muted-foreground/35" />
      <Icon className="shrink-0" style={{ width: fontSizePx, height: fontSizePx }} />
      <span className="truncate" style={{ fontSize: `${fontSizePx}px` }}>
        {label}
      </span>
    </button>
  );
}

// Level 3: what the row shows once it is opened — the command that ran and its output.
function workEntryDetailHeading(workEntry: TimelineWorkEntry): string {
  if (workEntry.compaction) {
    return "Summary the agent sees";
  }
  if (workEntry.itemType === "command_execution" || workEntry.requestKind === "command") {
    return "Shell";
  }
  return toolWorkEntryHeading(workEntry);
}

function hasWorkEntryDetail(workEntry: TimelineWorkEntry): boolean {
  // A subagent is worth opening from the moment it starts: the prompt it was
  // given is the whole point, its report only lands at the end, and a Codex one
  // opens onto the threads where its transcript actually lives.
  return (
    Boolean(workEntry.command) ||
    Boolean(workEntry.output) ||
    Boolean(workEntry.subagentTask?.prompt) ||
    (workEntry.subagentTask?.threadLinks?.length ?? 0) > 0
  );
}

/** Level 3 status line for a subagent row: where it is and what it is doing. */
function subagentTaskStatusLine(
  subagentTask: NonNullable<TimelineWorkEntry["subagentTask"]>,
): string {
  switch (subagentTask.status) {
    case "running":
      return subagentTask.currentAction ? `Running · ${subagentTask.currentAction}` : "Running";
    case "failed":
      return "Failed";
    default:
      return "Completed";
  }
}

function isFailedWorkEntry(workEntry: TimelineWorkEntry): boolean {
  return (
    workEntry.isError === true || (workEntry.exitCode !== undefined && workEntry.exitCode !== 0)
  );
}

/**
 * Previews of the files a local action sent back from the user's machine, shown
 * inside the tool detail panel right under the output that lists them: the image
 * belongs with its text, not in a gallery of its own somewhere else in the row.
 *
 * When one fails to load the row shows its `/attachments/<id>` link instead of a
 * mute gap — otherwise the reader cannot tell an upload that never happened from
 * an image the browser could not fetch. The link opens in a new tab; inside the
 * desktop client that hands the URL to the system browser, which may not carry
 * the session cookie, so it is a fallback and never the primary way to look at a
 * capture (that is the in-app viewer below).
 */
const WorkEntryAttachmentImages = memo(function WorkEntryAttachmentImages(props: {
  images: ReadonlyArray<WorkEntryAttachmentImage>;
  fontSizePx: number;
  onImageExpand?: ((preview: ExpandedImagePreview) => void) | undefined;
  onImageSettled?: (() => void) | undefined;
}) {
  const [failedIds, setFailedIds] = useState<ReadonlySet<string>>(() => new Set<string>());
  const { onImageSettled } = props;
  const captionFontSizePx = Math.max(10, props.fontSizePx - 1);
  return (
    <div className="mt-1.5 flex flex-wrap items-start gap-2">
      {props.images.map((image) => {
        const failed = failedIds.has(image.attachmentId);
        const preview = props.onImageExpand
          ? buildExpandedImagePreview(
              [{ id: image.attachmentId, name: image.name, previewUrl: image.path }],
              image.attachmentId,
            )
          : null;
        const picture = (
          <img
            src={image.path}
            alt={image.name}
            loading="lazy"
            className="max-w-full rounded-md border border-border/45 object-contain"
            // Not `max-h-40`: the height estimator budgets this same number for a
            // preview that has not loaded yet, so it has to come from one place.
            style={{ maxHeight: `${WORK_ENTRY_ATTACHMENT_PREVIEW_MAX_HEIGHT_PX}px` }}
            onLoad={() => onImageSettled?.()}
            onError={() => {
              setFailedIds((previous) => {
                if (previous.has(image.attachmentId)) return previous;
                const next = new Set(previous);
                next.add(image.attachmentId);
                return next;
              });
              onImageSettled?.();
            }}
          />
        );
        return (
          <figure key={`work-attachment:${image.attachmentId}`} className="min-w-0">
            {failed ? (
              <a
                href={image.path}
                target="_blank"
                rel="noopener noreferrer"
                className="font-chat-code block break-all rounded-md border border-dashed border-border/45 px-2 py-1 text-muted-foreground/55 underline decoration-dotted underline-offset-2"
                style={{ fontSize: `${captionFontSizePx}px` }}
                onClick={(event) => event.stopPropagation()}
              >
                {image.path}
              </a>
            ) : preview && props.onImageExpand ? (
              <button
                type="button"
                aria-label={`Ampliar ${image.name}`}
                onClick={(event) => {
                  event.stopPropagation();
                  props.onImageExpand?.(preview);
                }}
              >
                {picture}
              </button>
            ) : (
              picture
            )}
            <figcaption
              className="mt-0.5 max-w-40 truncate text-muted-foreground/40"
              style={{ fontSize: `${captionFontSizePx}px` }}
              title={image.name}
            >
              {failed ? `${image.name} · no se pudo cargar` : image.name}
            </figcaption>
          </figure>
        );
      })}
    </div>
  );
});

const SimpleWorkEntryRow = memo(function SimpleWorkEntryRow(props: {
  workEntry: TimelineWorkEntry;
  chatMetaFontSizePx: number;
  textFontSizePx?: number;
  density?: "default" | "compact";
  fileDiffStatByPath?: ReadonlyMap<string, { additions: number; deletions: number }>;
  turnId?: TurnId;
  expanded?: boolean;
  onToggleExpanded?: (entryId: string) => void;
  onOpenTurnDiff?: (turnId: TurnId, filePath?: string) => void;
  onOpenThread?: (threadId: ThreadId) => void;
  openUserInputRequestIds?: ReadonlySet<string>;
  onReopenUserInput?: (requestId: string) => void;
  onImageExpand?: (preview: ExpandedImagePreview) => void;
  /** Lets the virtualizer remeasure once a capture settles and changes the row height. */
  onImageSettled?: () => void;
}) {
  const {
    workEntry,
    chatMetaFontSizePx,
    textFontSizePx = chatMetaFontSizePx,
    density = "default",
    fileDiffStatByPath,
    turnId,
    expanded = false,
    onToggleExpanded,
    onOpenTurnDiff,
    onOpenThread,
    openUserInputRequestIds,
    onReopenUserInput,
    onImageExpand,
    onImageSettled,
  } = props;
  const compact = density === "compact";
  const EntryIcon = workEntryIcon(workEntry);
  const usesTrailingCompactIcon =
    EntryIcon === TerminalIcon || EntryIcon === HammerIcon || EntryIcon === AgentTaskIcon;
  const showIconRight = compact && usesTrailingCompactIcon;
  const showIconLeft = !compact;
  // A compaction's headline and trailing "tokens saved" note come off its
  // structured status, not the generic label/detail parsing below — the
  // server's free-text label varies by provider and cycle stage, but the
  // status is the same shape everywhere.
  const heading = workEntry.compaction
    ? compactionHeading(workEntry.compaction)
    : toolWorkEntryHeading(workEntry);
  const preview = workEntry.compaction
    ? compactionSavedSuffix(workEntry.compaction)
    : workEntryPreview(workEntry);
  const compactionMeta = workEntry.compaction ? compactionDetailMeta(workEntry.compaction) : null;
  const displayText = preview ? `${heading} ${preview}` : heading;
  const changedFiles = workEntry.changedFiles ?? [];
  const showEditedRows = isFileChangeWorkEntry(workEntry) && changedFiles.length > 0;
  // An in-progress compaction has nothing to show yet — it never carries a
  // summary — so its row stays non-expandable even if a stray detail slips in.
  // A row announcing a question the agent is still parked on takes over its own
  // click: reopening the question beats showing the raw tool payload, which is
  // what expanding this row would otherwise do.
  const reopenUserInputRequestId =
    workEntry.userInputRequestId && openUserInputRequestIds?.has(workEntry.userInputRequestId)
      ? workEntry.userInputRequestId
      : null;
  const canReopenUserInput = Boolean(reopenUserInputRequestId && onReopenUserInput);

  // A question gets a row of its own: the generic tool row can only show the
  // label the server stored ("User input requested"), which says nothing about
  // what was asked or what was decided — and that decision is exactly what
  // someone rereading the thread came for.
  if (workEntry.userInput) {
    return (
      <div className={cn(compact ? "py-0.5" : "py-1")}>
        <UserInputWorkEntryRow
          userInput={workEntry.userInput}
          fontSizePx={textFontSizePx}
          metaFontSizePx={chatMetaFontSizePx}
          expanded={expanded}
          onToggleExpanded={onToggleExpanded ? () => onToggleExpanded(workEntry.id) : undefined}
          canReopen={canReopenUserInput}
          onReopen={
            reopenUserInputRequestId && onReopenUserInput
              ? () => onReopenUserInput(reopenUserInputRequestId as string)
              : undefined
          }
        />
      </div>
    );
  }
  const canExpand =
    !canReopenUserInput &&
    Boolean(onToggleExpanded) &&
    hasWorkEntryDetail(workEntry) &&
    workEntry.compaction?.status !== "inProgress";
  const failed = isFailedWorkEntry(workEntry) || workEntry.compaction?.status === "failed";
  // A subagent runs unattended for minutes, so its row spins while it works. The
  // icon identity above stays the agent glyph: it drives the layout decisions.
  // A compaction in flight spins the same way — there is no partial progress to
  // show, just the fact that it is happening.
  const subagentRunning = workEntry.subagentTask?.status === "running";
  const compactionRunning = workEntry.compaction?.status === "inProgress";
  const RowIcon = subagentRunning || compactionRunning ? LoaderIcon : EntryIcon;

  // Use the text font size (matching the UI settings) for tool call rows
  const rowFontSizePx = textFontSizePx;
  // Captures a local action uploaded, read off the very output lines below.
  const attachmentImages = extractWorkEntryAttachmentImages(workEntry.output);

  return (
    <div className={cn(compact ? "py-0.5" : "rounded-lg py-1")}>
      {showEditedRows ? (
        <div className="space-y-0.5">
          {changedFiles.map((changedFilePath) => {
            const changedFileStat = fileDiffStatByPath?.get(changedFilePath);
            const canOpenEditedDiff = Boolean(turnId && onOpenTurnDiff);
            return (
              <button
                key={`${workEntry.id}:${changedFilePath}`}
                type="button"
                className={cn(
                  "group flex w-full max-w-full items-baseline gap-1 text-left transition-opacity duration-150",
                  compact
                    ? "px-0 py-[1px] hover:opacity-95"
                    : "rounded-md border border-border/45 bg-background/65 px-2 py-1 hover:bg-background/80",
                  canOpenEditedDiff ? "cursor-pointer" : "cursor-default",
                )}
                title={changedFilePath}
                disabled={!canOpenEditedDiff}
                onClick={() => {
                  if (!turnId || !onOpenTurnDiff) return;
                  onOpenTurnDiff(turnId, changedFilePath);
                }}
              >
                <span
                  className="font-system-ui shrink-0 text-muted-foreground/60"
                  style={{ fontSize: `${rowFontSizePx}px` }}
                >
                  Edited
                </span>
                <span
                  className="font-system-ui max-w-[28rem] truncate group-hover:opacity-90"
                  style={{
                    fontSize: `${rowFontSizePx}px`,
                    color: "var(--info-foreground)",
                  }}
                >
                  {basename(changedFilePath)}
                </span>
                {changedFileStat ? (
                  <span
                    className="font-chat-code shrink-0 tabular-nums whitespace-nowrap"
                    style={{ fontSize: `${rowFontSizePx}px` }}
                  >
                    <DiffStatLabel
                      additions={changedFileStat.additions}
                      deletions={changedFileStat.deletions}
                    />
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      ) : (
        <>
          <button
            type="button"
            disabled={!canExpand && !canReopenUserInput}
            title={displayText}
            {...(canExpand ? { "aria-expanded": expanded } : {})}
            onClick={
              canReopenUserInput
                ? () => onReopenUserInput?.(reopenUserInputRequestId as string)
                : canExpand
                  ? () => onToggleExpanded?.(workEntry.id)
                  : undefined
            }
            className={cn(
              "flex w-full items-center text-left transition-[opacity,translate] duration-200",
              compact ? "gap-1.5" : "gap-2",
              canExpand || canReopenUserInput
                ? "cursor-pointer hover:opacity-90"
                : "cursor-default",
            )}
          >
            {showIconLeft && (
              <span
                className={cn(
                  "flex shrink-0 items-center justify-center",
                  failed ? "text-rose-400/60" : "text-muted-foreground/40",
                  compact ? "size-4" : "size-5",
                )}
              >
                <RowIcon
                  className={cn(
                    compact ? "size-2.5" : "size-3",
                    (subagentRunning || compactionRunning) && "animate-spin",
                  )}
                />
              </span>
            )}
            <div className="min-w-0 flex-1 overflow-hidden">
              <span
                className={cn(
                  "block",
                  compact ? "truncate leading-5" : "truncate leading-6",
                  "text-muted-foreground/50",
                )}
                style={{ fontSize: `${rowFontSizePx}px` }}
              >
                <span className={failed ? "text-rose-400/70" : "text-muted-foreground/50"}>
                  {heading}
                </span>
                {preview && (
                  <span
                    className={cn(
                      "text-muted-foreground/25",
                      workEntry.command && "font-chat-code",
                    )}
                  >
                    {" "}
                    {preview}
                  </span>
                )}
                {/* Without this the row looks like every other inert log line,
                    and nothing suggests the question can be brought back. */}
                {canReopenUserInput && <span className="text-brand-foreground/70"> · Answer</span>}
              </span>
            </div>
            {showIconRight && (
              <span
                className={cn(
                  "flex shrink-0 items-center justify-center",
                  failed ? "text-rose-400/60" : "text-muted-foreground/40",
                )}
                style={{ width: rowFontSizePx, height: rowFontSizePx }}
              >
                <RowIcon
                  className={subagentRunning || compactionRunning ? "animate-spin" : undefined}
                  style={{ width: rowFontSizePx, height: rowFontSizePx }}
                />
              </span>
            )}
          </button>
          {/* Outside the collapsible below on purpose: a capture the agent took
              is the answer, not a detail of it. Inside, the row's `grid-rows-[0fr]`
              rendered it at zero height, so the agent announced an image nobody
              could see unless they happened to expand the row. Indented to the
              heading's text column, which the leading icon opens up. */}
          {attachmentImages.length > 0 && (
            <div className={compact ? undefined : "pl-7"}>
              <WorkEntryAttachmentImages
                images={attachmentImages}
                fontSizePx={rowFontSizePx}
                {...(onImageExpand ? { onImageExpand } : {})}
                {...(onImageSettled ? { onImageSettled } : {})}
              />
            </div>
          )}
          {canExpand && (
            // Same grid-rows animation as the "Files changed" block: inside the
            // virtualizer a real collapsible fights measureElement.
            <div
              className={cn(
                "grid transition-[grid-template-rows,opacity] duration-200 ease-out",
                expanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
              )}
            >
              <div
                className={cn("min-h-0 overflow-hidden", expanded ? null : "pointer-events-none")}
              >
                <div
                  className={cn(
                    "mt-1 overflow-hidden rounded-md border bg-background/60",
                    failed ? "border-rose-500/25" : "border-border/45",
                  )}
                  data-tool-detail-panel="true"
                >
                  <div className="flex items-center justify-between gap-2 border-b border-border/35 px-2 py-1">
                    <span
                      className="truncate text-muted-foreground/55"
                      style={{ fontSize: `${rowFontSizePx}px` }}
                    >
                      {workEntryDetailHeading(workEntry)}
                    </span>
                    {workEntry.compaction
                      ? compactionMeta && (
                          <span
                            className="font-chat-code shrink-0 tabular-nums text-muted-foreground/40"
                            style={{ fontSize: `${Math.max(10, rowFontSizePx - 1)}px` }}
                          >
                            {compactionMeta}
                          </span>
                        )
                      : workEntry.exitCode !== undefined && (
                          <span
                            className={cn(
                              "font-chat-code shrink-0 tabular-nums",
                              failed ? "text-rose-400/75" : "text-muted-foreground/40",
                            )}
                            style={{ fontSize: `${Math.max(10, rowFontSizePx - 1)}px` }}
                          >
                            exit {workEntry.exitCode}
                          </span>
                        )}
                  </div>
                  <div className="px-2 py-1.5">
                    {workEntry.subagentTask && (
                      <>
                        <p
                          className={cn(
                            "pb-1",
                            failed ? "text-rose-400/70" : "text-muted-foreground/45",
                          )}
                          style={{ fontSize: `${Math.max(10, rowFontSizePx - 1)}px` }}
                        >
                          {subagentTaskStatusLine(workEntry.subagentTask)}
                        </p>
                        {workEntry.subagentTask.prompt && (
                          <>
                            <p
                              className="uppercase tracking-[0.14em] text-muted-foreground/30"
                              style={{ fontSize: `${Math.max(10, rowFontSizePx - 2)}px` }}
                            >
                              Prompt
                            </p>
                            <pre
                              className="font-chat-code overflow-auto whitespace-pre-wrap break-words text-muted-foreground/55"
                              style={{
                                fontSize: `${rowFontSizePx}px`,
                                maxHeight: `${WORK_ENTRY_DETAIL_MAX_HEIGHT_PX}px`,
                              }}
                            >
                              {workEntry.subagentTask.prompt}
                            </pre>
                          </>
                        )}
                        {(workEntry.subagentTask.threadLinks?.length ?? 0) > 0 && (
                          <div className="flex flex-wrap items-center gap-1.5 pt-1.5">
                            {workEntry.subagentTask.threadLinks?.map((link) => (
                              <button
                                key={`${workEntry.id}:${link.threadId}`}
                                type="button"
                                className={cn(
                                  "shrink-0 rounded-full border border-border/45 px-2.5 py-1 text-[9px] font-medium uppercase tracking-[0.12em] text-muted-foreground/62 transition-colors",
                                  onOpenThread
                                    ? "hover:border-foreground/15 hover:text-foreground/84"
                                    : "cursor-default opacity-50",
                                )}
                                disabled={!onOpenThread}
                                title={link.label}
                                onClick={(event) => {
                                  // The whole row is a toggle; opening a thread
                                  // must not also collapse the panel behind it.
                                  event.stopPropagation();
                                  onOpenThread?.(ThreadId.makeUnsafe(link.threadId));
                                }}
                              >
                                Open {link.label}
                              </button>
                            ))}
                          </div>
                        )}
                        {workEntry.output && (
                          <p
                            className="pt-1.5 uppercase tracking-[0.14em] text-muted-foreground/30"
                            style={{ fontSize: `${Math.max(10, rowFontSizePx - 2)}px` }}
                          >
                            Result
                          </p>
                        )}
                      </>
                    )}
                    {workEntry.command && (
                      <p
                        className="font-chat-code break-all text-muted-foreground/60"
                        style={{ fontSize: `${rowFontSizePx}px` }}
                      >
                        <span className="text-muted-foreground/35">$ </span>
                        {workEntry.command}
                      </p>
                    )}
                    {workEntry.output && (
                      <pre
                        className="font-chat-code mt-1 overflow-auto whitespace-pre-wrap break-all text-muted-foreground/45"
                        style={{
                          fontSize: `${rowFontSizePx}px`,
                          maxHeight: `${WORK_ENTRY_DETAIL_MAX_HEIGHT_PX}px`,
                        }}
                      >
                        {workEntry.output}
                      </pre>
                    )}
                    {workEntry.outputTruncated && (
                      <p
                        className="pt-1 text-muted-foreground/30"
                        style={{ fontSize: `${Math.max(10, rowFontSizePx - 1)}px` }}
                      >
                        Output truncated
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
});
